/**
 * countdownEvents/{id} - Kim-managed list of studio milestones
 * (competitions, recitals, and other dates worth counting down to).
 *
 * One list is meant to power countdown pills everywhere in the Hub, the
 * Bag Group drop-off auto-fill (pick a competition, get a drop-off date
 * seven days out), and dashboards. Kept intentionally small: a name, an
 * emoji, a color, a date, who should see it, and what kind of event it is.
 *
 * Rules: any active signed-in user can read the whole list (the
 * `visibility` field is a client-side display filter, not a security
 * boundary - see shared/firestore.rules). Director and Manager ONLY can
 * create/update/delete (Bobbie, 2026-08-15: "Director + Manage yes! on
 * countdown" - FOH was deliberately moved out of this lane then; the
 * client mirror is canManageCountdowns in roleCapabilities.ts). This
 * header used to still name FOH, a leftover from the openHours-matching
 * era; corrected 2026-08-19 after a review caught the drift.
 *
 * Packet 24 (Bobbie, 2026-08-14 night, "It would also be nice to be able to
 * add the countdowns in advance... start to display 7,14,30,60,90 days in
 * advance so I can line them all up now") added two optional fields:
 * startShowingDaysBefore (a display WINDOW for the pill, absent = always
 * shows, matching every countdown that existed before this field did) and
 * details (free text, rendered wherever a date opens). Neither changes an
 * existing countdown's behavior unless someone sets it.
 *
 * STUDIO-WIDE EVENTS (Bobbie, 2026-08-15: "I also need to be able to add
 * events that touch all classes. LIke parent watch week needs to show up as
 * a pill on all classes during that week. and also on the calender. I need
 * to be able to put in comp weekend that do not book a room, but everyone
 * needs on tehir calendar. and recital dates.") added two more optional
 * fields to THIS collection rather than inventing a second one, because the
 * editor, the audience machinery, the details field and the display windows
 * already live here:
 *
 *  - `endDate` ('YYYY-MM-DD', never earlier than `date`). Absent means a
 *    single-day event, which is every countdown written before today, and
 *    every helper below treats an absent endDate as `date` so single-day
 *    behavior is unchanged byte for byte.
 *  - `showOnClasses` (bool). Absent means false, so no existing countdown
 *    starts appearing on class pages. When true, the event renders as a pill
 *    on every class page and class card whose meeting falls inside the range.
 *
 * Neither field books a room: nothing here writes a reservation, which is
 * the whole point of "comp weekend that do not book a room".
 */

import { useEffect, useMemo, useState } from 'react';
import {
  addDoc,
  arrayUnion,
  collection,
  deleteField,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  updateDoc,
  where,
  type FieldValue,
  type Timestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import {
  EVENT_REPEAT_CHOICES,
  isValidCountdownDate,
  normalizeEventKind,
  normalizeEventVisibility,
  type EventRepeatChoice,
} from './eventCore.ts';
export { EVENT_REPEAT_CHOICES, isValidCountdownDate, type EventRepeatChoice } from './eventCore.ts';
import type { User } from 'firebase/auth';
import { useAuth } from './auth-context';
import {
  audienceUidsFromActiveUsers,
  notifyUsers,
  type NotifyDeliveryResult,
  type NotifyPayload,
} from './notifications';
import type { UserRole } from './users';
import { OVERLAY_COLLECTION, record as overlayRecord, stringList as overlayStringList } from './boardMembershipOverlay';
import { safeRenderableAttachmentUrl } from './saveAttachment';
import {
  normalizeEventAbout,
  validatedEventAboutInput,
  type EventAboutData,
  type EventAboutInput,
} from './eventAbout';

export type CountdownVisibility = 'everyone' | 'comp' | 'staff';
export type CountdownKind = 'competition' | 'recital' | 'other';
/** The two answers on a comp date. No third state: absent IS "has not said". */
export type CompDateRsvp = 'going' | 'cant';

/** Preset "start showing the pill" windows. Absent field on the doc means
 * always-show, the behavior every countdown had before this field existed. */
export const COUNTDOWN_WINDOW_CHOICES = [7, 14, 30, 60, 90] as const;
export type CountdownWindowChoice = (typeof COUNTDOWN_WINDOW_CHOICES)[number];

export const COUNTDOWN_WINDOW_LABELS: Record<CountdownWindowChoice, string> = {
  7: '7 days before',
  14: '14 days before',
  30: '30 days before',
  60: '60 days before',
  90: '90 days before',
};

export const COUNTDOWN_DETAILS_MAX = 1000;

/** The four long-form event-page panels, each with its own typed text box. */
export const EVENT_PANEL_FIELDS = [
  'scheduleInfo',
  'aboutText',
  'hotelInfo',
  'travelInfo',
] as const;
export type EventPanelField = (typeof EVENT_PANEL_FIELDS)[number];

/**
 * AN EVENT PAGE PUBLISHES ONCE (Bobbie, 2026-08-26, DECISIONS.md point 3).
 * Schedule, hotel, travel and about are edited freely with NO alert; the
 * page remembers what changed in `eventV2.unpublished` (a list of these keys,
 * inside the eventV2 map so it costs no rules bytes) and "Publish updates"
 * sends ONE notification and clears it. Content is visible to families as
 * soon as it is saved; only the ALERT waits.
 */
export const EVENT_PUBLISH_KEYS = [...EVENT_PANEL_FIELDS, 'about'] as const;
export type EventPublishKey = (typeof EVENT_PUBLISH_KEYS)[number];

/**
 * THE EVENT PAGE'S COVER PHOTO (Bobbie, 2026-09-02, live, looking at the
 * Nationals page: "I don't see a way to actually add a photo to the cover
 * photo and we need one... It's just a big blank space with color").
 *
 * It is a FIFTH PHOTO SLOT, not a fifth text panel. The four names above
 * each pair a typed box with an optional photo; 'coverPhoto' has no text
 * box at all, it is only the hero image at the top of the page. So the
 * photo vocabulary is its own list rather than a widened EVENT_PANEL_FIELDS
 * - that keeps StaffInfoBlock, PANEL_LABELS and PANEL_PROMPTS in
 * CompetitionEvent.tsx describing exactly the four boxes they really are.
 *
 * THIS COSTS ZERO RULES BYTES, which is why it is a key inside
 * eventV2.panelPhotos and not a new top-level coverPhoto field. Stage B
 * (2026-08-20) collapsed the v2 field allowances in shared/firestore.rules
 * into one `data.eventV2 is map` gate, and panelPhotos is already
 * server-owned there: keepsPanelPhotos() makes a Director/Manager client
 * edit preserve the whole map byte for byte, and the ONLY writer is the
 * countdownEventPanelPhoto lane in the attachment gateway. A new key inside
 * that map is a client-and-server change with no rules change at all. Do
 * NOT "improve" this by promoting it to its own field or its own document;
 * either would spend real bytes for nothing and open a second write door.
 */
export const EVENT_PANEL_PHOTO_FIELDS = [
  ...EVENT_PANEL_FIELDS,
  'coverPhoto',
] as const;
export type EventPanelPhotoField = (typeof EVENT_PANEL_PHOTO_FIELDS)[number];

export interface EventPanelPhoto {
  id: string;
  kind: 'image';
  url: string;
  name: string;
  contentType: 'image/jpeg' | 'image/png' | 'image/gif' | 'image/webp';
  storagePath: string;
}

export type EventPanelPhotos = Partial<Record<EventPanelPhotoField, EventPanelPhoto>>;

export interface CountdownEventDoc {
  /** Absent preserves existing home countdowns; false keeps only calendar entries. */
  countdownEnabled?: boolean;
  id: string;
  name: string;
  /** A single emoji (or short glyph), max 8 chars. */
  emoji: string;
  /** Hex color, '#RRGGBB'. */
  color: string;
  /** 'YYYY-MM-DD'. The START of the event when it spans days. EMPTY STRING
   * when `dateTbd` is true: a TBD date has no date at all (never a
   * placeholder day), so never do date math on an event before checking
   * `dateTbd`. The live list (useCountdownEvents) never holds one; only
   * useTbdCountdownEvents does. */
  date: string;
  /** True = "Date to be confirmed". Stored as eventV2.dateTbd with NO `date`
   * and NO `endDate` on the doc. Absent = a normal dated event. */
  dateTbd?: boolean;
  /** 'YYYY-MM-DD', never earlier than `date`. Absent = a single-day event. */
  endDate?: string;
  /** True = show a pill on every class inside the range. Absent = false. */
  showOnClasses?: boolean;
  visibility: CountdownVisibility;
  kind: CountdownKind;
  /** Absent = always show the pill. Present = wait until this many days out. */
  startShowingDaysBefore?: CountdownWindowChoice;
  /** Free-text notes, rendered in the Important Dates view and wherever the
   * date opens. Absent or empty means nothing to show. */
  details?: string;
  /** WHERE the event is, one typed line ("venue, city"), per Bobbie's comp
   * event page canvas (docs/design/comp-event-page/, 2026-08-19: the header
   * is name, when, where, and the share flow promises "the date, the
   * venue"). One free-text line rather than split name/city fields so the
   * screen prints exactly what Kim typed. Absent or empty = the event
   * page's honest no-venue-entered line. */
  venue?: string;
  /**
   * Comp Board v2, packet CB-C: who on the comp team is going.
   *
   * uid -> 'going' | 'cant', written one key at a time by the person whose key
   * it is (shared/firestore.rules, the isMyCountdownRsvp lane). Absent on every
   * date written before this field existed, and absent on any date nobody has
   * answered yet, so every reader treats missing as nobody-has-said.
   *
   * It lives on the date itself rather than in a collection of its own because
   * these documents ARE the comp dates: the board's DATES tab, every countdown
   * pill and the important-dates editor all read this one list, and a second
   * collection would be a second answer to which comps exist.
   */
  rsvps?: Record<string, CompDateRsvp>;
  /* ---- Event model v2 (calendar redesign slice 1). ALL optional; nothing
   * renders these yet. Absent = the pre-v2 behavior, field by field. ---- */
  /** The canvas's event type. Absent = untyped (legacy `kind` still governs
   * every existing surface; the composer will keep the two coherent). */
  eventType?: EventTypeChoice;
  /** eventTypes/{id} when eventType is 'custom'. A dangling id renders as a
   * plain event; the client resolves it, the rules deliberately do not (a
   * cross-document existence check would spend a document access). */
  customTypeId?: string;
  /** 'HH:MM' 24h event start on `date`. Absent = no time set (all existing
   * dates). */
  startTime?: string;
  /** 'HH:MM' 24h end. With no endDate it ends the same day. */
  endTime?: string;
  /** Explicit all-day flag for the composer's toggle. Absent = false. */
  allDay?: boolean;
  /** Minutes dancers arrive before start (the call is start minus this).
   * Absent = no separate call time. */
  arriveMinutesBefore?: number;
  /** Per-class call times: offeringId -> 'HH:MM'. Overrides
   * arriveMinutesBefore for the classes it names. */
  callTimes?: Record<string, string>;
  /** The classes this date applies to (offering ids). Absent = studio-wide,
   * which is what every pre-v2 date is. */
  appliesTo?: string[];
  /** "Amount due at the front desk", one typed line printed as typed (the
   * same screen-prints-what-Kim-typed reasoning venue records). Not a
   * number on purpose: nothing does money math with it. */
  amountDueNote?: string;
  /** Fee deadline in studio calendar days, independent of the event date. */
  feeDueDate?: string;
  /**
   * THE FOUR PANELS AN EVENT PAGE LETS STAFF TYPE (Bobbie, 2026-08-26:
   * "I want to be able to edit and add hotel info travel info and other
   * details"). Long free text, printed as typed. `scheduleInfo` is the
   * event-page replacement for the short general-countdown `details` field;
   * old events fall back to `details` until Schedule is saved once. An
   * explicitly cleared Schedule is stored as an empty-string override so
   * the legacy details text does not reappear. The same
   * screen-prints-what-Kim-typed reasoning `venue` records.
   *
   * THESE COST ZERO RULES BYTES, which is the only reason they are four
   * fields rather than one overloaded box. Stage B (2026-08-20) collapsed
   * nineteen flat v2 field allowances in shared/firestore.rules into a
   * single `data.eventV2 is map` gate, deliberately moving the per-field
   * vocabulary to this file's own write boundary
   * (validatedEventModelV2Fields). So a new key inside eventV2 is a client
   * change, and the release ceiling - which refused this file's ruleset
   * twice this week - is untouched. Do NOT "improve" this by promoting
   * them to top-level fields; that would cost real bytes for nothing.
   */
  scheduleInfo?: string;
  hotelInfo?: string;
  travelInfo?: string;
  aboutText?: string;
  /** One server-owned photo per long-form panel. This is read-only to the
   * ordinary event editor; the attachment gateway owns every mutation. */
  panelPhotos?: EventPanelPhotos;
  /**
   * THE ABOUT TAB'S SMART EDITOR (Bobbie, 2026-09-02: "I need a teachers
   * section here that directors and managers can edit and add things... a
   * smart editor for all of these.... again similar to facebook events").
   * Named facts plus Director-authored sections (Teachers first among
   * them, by convention - see eventAbout.ts). Whole-object write, same
   * ZERO-RULES-BYTES reasoning as the four panels above: it lives inside
   * eventV2, which is `is map` only.
   */
  about?: EventAboutData;
  /** Event-page parts saved since the last "Publish updates". Staff-facing;
   * absent = nothing waiting. Lives at eventV2.unpublished. */
  unpublished?: EventPublishKey[];
  /** True = families are asked to answer (the reminder machinery is a later
   * slice; the flag is the model's part). Absent = false. */
  rsvpRequired?: boolean;
  /** Where the class-card pill shows when showOnClasses is true: 'all'
   * (every class, the pre-v2 meaning and the absent default) or 'tagged'
   * (only the appliesTo classes). */
  pillScope?: EventPillScope;
  /** The page this date starts (Turn 6). MODEL ONLY: no URL exists until
   * Bobbie's B3 ruling; pageKind absent = no page. */
  pageKind?: EventPageKind;
  /** URL-safe handle for the page, minted with pageKind. Model only. */
  slug?: string;
  /** Recurrence. Absent = does not repeat. A/B choices follow MARKED weeks
   * only (abWeekCalendar), skipping unmarked, never guessing. */
  repeat?: EventRepeatChoice;
  /** 'YYYY-MM-DD' last day the repeat can land on, never before `date`. */
  repeatUntil?: string;
  /** The countdownEvents id this date lives INSIDE (hotel block inside a
   * comp trip). A dangling parent renders as a plain date. */
  parentEventId?: string;
  /** The countdown block's headline ("NATIONALS WEEK"). Absent = the event
   * name leads, unchanged. */
  countdownHeadline?: string;
  /** What the countdown counts to. Absent = 'start', the only pre-v2
   * behavior. */
  countdownTarget?: EventCountdownTarget;
  /** How the countdown prints. Absent = 'days', the only pre-v2 behavior. */
  countdownUnits?: EventCountdownUnits;
  createdAt: Timestamp | FieldValue;
  createdBy: string;
  updatedAt: Timestamp | FieldValue;
}

/** Plain-words labels for the visibility select. */
export const VISIBILITY_LABELS: Record<CountdownVisibility, string> = {
  everyone: 'Everyone',
  comp: 'Comp families + staff',
  staff: 'Staff only',
};

export const KIND_LABELS: Record<CountdownKind, string> = {
  competition: 'Competition',
  recital: 'Recital',
  other: 'Other',
};

/** Handy starting palette so the color picker isn't blank. Same family as ROOM_COLOR_PRESETS. */
export const COUNTDOWN_COLOR_PRESETS: { label: string; hex: string }[] = [
  { label: 'Lime', hex: '#A8E63A' },
  { label: 'Rose', hex: '#FF6B8A' },
  { label: 'Lavender', hex: '#B59CFF' },
  { label: 'Tangerine', hex: '#FFB14B' },
  { label: 'Sky', hex: '#5EC8FF' },
  { label: 'Slate', hex: '#8FA0B5' },
];

const COLOR_RE = /^#[0-9A-Fa-f]{6}$/;


export function isValidHexColor(value: unknown): value is string {
  return typeof value === 'string' && COLOR_RE.test(value);
}

export function isValidCountdownWindowChoice(value: unknown): value is CountdownWindowChoice {
  return typeof value === 'number' && (COUNTDOWN_WINDOW_CHOICES as readonly number[]).includes(value);
}

/** Bounded-length check for the details field; empty string is valid (means "no details"). */
export function isValidCountdownDetails(value: unknown): value is string {
  return typeof value === 'string' && value.length <= COUNTDOWN_DETAILS_MAX;
}

/** One typed line, matching the m0_120 ceiling in shared/firestore.rules -
 * the two MUST move together, same contract details has with m0_1000. */
export const COUNTDOWN_VENUE_MAX = 120;

/** Bounded-length check for the venue line; empty string is valid (means "no venue entered"). */
export function isValidCountdownVenue(value: unknown): value is string {
  return typeof value === 'string' && value.length <= COUNTDOWN_VENUE_MAX;
}

/* ------------------------------------------------------------------------ *
 * EVENT MODEL V2 (calendar redesign slice 1, Bobbie's canvas
 * docs/design/calendar-redesign/, 2026-08-19: "it is a major overhaul so
 * please look carefully"; sequencing in
 * docs/calendar-redesign-plan-2026-08-19.md).
 *
 * Every field below is OPTIONAL and absent-safe: a countdown written before
 * tonight is valid unchanged, and nothing in the app renders any of these
 * yet. The composer (slice 2) writes them; the views (slices 3-6) read
 * them. Since Stage B of the 2026-08-20 release-cliff incident these fields
 * live under ONE eventV2 map that shared/firestore.rules checks only as
 * `is map` (hasEventModelV2Fields is gone), so the enums and ceilings below
 * are enforced at this client write boundary and re-checked by every reader.
 *
 * Two gaps left VISIBLE rather than guessed (the canvas lists them, their
 * semantics are not settled):
 *  - the 'custom' REPEAT choice (canvas Turn 5 offers it; what a custom
 *    recurrence means is undefined, so the enum stops at monthly and the
 *    composer will not offer custom until it is);
 *  - attachments (they need the upload gateway wiring, which belongs to the
 *    slice that builds the composer, so the field ships with its writer).
 * ------------------------------------------------------------------------ */

/** The canvas's event types (Turn 5). 'custom' points at eventTypes/{customTypeId}.
 * Picnic and Other are Bobbie's ask for the create and edit picker; both count
 * as kind 'other' there, like every type that is not a competition or recital.
 * The rules do not list these values (eventV2 is one `is map` gate), so a new
 * choice here needs no rules change; the rules suite probes every one. */
export const EVENT_TYPE_CHOICES = [
  'competition',
  'recital',
  'rehearsal',
  'deadline',
  'picture-day',
  'registration',
  'parent-meeting',
  'audition',
  'workshop',
  'closure',
  'fundraiser',
  'picnic',
  'other',
  'custom',
] as const;
export type EventTypeChoice = (typeof EVENT_TYPE_CHOICES)[number];

export const EVENT_TYPE_LABELS: Record<EventTypeChoice, string> = {
  competition: 'Competition',
  recital: 'Recital',
  rehearsal: 'Rehearsal',
  deadline: 'Deadline or payment',
  'picture-day': 'Picture day',
  registration: 'Registration',
  'parent-meeting': 'Parent meeting',
  audition: 'Audition',
  workshop: 'Workshop',
  closure: 'Closure',
  fundraiser: 'Fundraiser',
  picnic: 'Picnic',
  other: 'Other',
  custom: 'Custom',
};

/** How a Director-created custom type behaves (eventTypes collection). */
export const EVENT_BEHAVES_LIKE_CHOICES = ['event', 'deadline', 'closure'] as const;
export type EventBehavesLike = (typeof EVENT_BEHAVES_LIKE_CHOICES)[number];


/** The page a published date starts (Turn 6). Model only in slice 1: no URL
 * is minted until Bobbie's B3 privacy ruling (plan doc). */
export const EVENT_PAGE_KINDS = ['comp-trip', 'recital', 'details'] as const;
export type EventPageKind = (typeof EVENT_PAGE_KINDS)[number];

/** What the countdown block counts to and how it prints (Turn 5). */
export const EVENT_COUNTDOWN_TARGETS = ['start', 'call'] as const;
export type EventCountdownTarget = (typeof EVENT_COUNTDOWN_TARGETS)[number];
export const EVENT_COUNTDOWN_UNITS = ['days', 'days-hours'] as const;
export type EventCountdownUnits = (typeof EVENT_COUNTDOWN_UNITS)[number];

/** Where a class-card pill shows: every class, or only the appliesTo list. */
export const EVENT_PILL_SCOPES = ['all', 'tagged'] as const;
export type EventPillScope = (typeof EVENT_PILL_SCOPES)[number];

const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;
/** 'HH:MM', 24-hour. The one time format the model stores; screens format it. */
export function isValidEventTime(value: unknown): value is string {
  return typeof value === 'string' && TIME_RE.test(value);
}

/** Lowercase letters, digits, hyphens, 3-80 chars, no edge hyphens. Mirror
 * of the rules regex; the slug is MODEL ONLY until B3 settles the URL lane. */
const SLUG_RE = /^[a-z0-9][a-z0-9-]{1,78}[a-z0-9]$/;
export function isValidEventSlug(value: unknown): value is string {
  return typeof value === 'string' && SLUG_RE.test(value);
}

export const EVENT_ARRIVE_BEFORE_MAX_MINUTES = 600;
export const EVENT_APPLIES_TO_MAX = 100;
export const EVENT_AMOUNT_DUE_MAX = 120;
/** Schedule, hotel, travel and about are long-form event-page panels. This
 * is deliberately independent from the short general-countdown `details`
 * ceiling: four full 50,000-character panels leave substantial document
 * headroom while making the limit a runaway-paste guard, not an editorial
 * limit. Bobbie's ruling was "no practical limit" (2026-08-26). */
export const EVENT_PANEL_MAX = 50_000;
export const EVENT_COUNTDOWN_HEADLINE_MAX = 80;

/** Minutes dancers arrive before start; 0..600 whole minutes. */
export function isValidArriveMinutesBefore(value: unknown): value is number {
  return (
    typeof value === 'number'
    && Number.isInteger(value)
    && value >= 0
    && value <= EVENT_ARRIVE_BEFORE_MAX_MINUTES
  );
}

/**
 * The per-class call times map, read side: offeringId -> 'HH:MM'. Junk
 * entries are dropped rather than rendered, the same posture
 * normalizeCompDateRsvps takes: a call time is a fact a family acts on, so
 * a malformed value must not become a phantom time. Returns undefined for
 * an empty or absent map so the doc keeps its pre-v2 shape.
 */
export function normalizeEventCallTimes(
  value: unknown
): Record<string, string> | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const out: Record<string, string> = {};
  for (const [offeringId, time] of Object.entries(value as Record<string, unknown>)) {
    if (offeringId && isValidEventTime(time)) out[offeringId] = time;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

/** The appliesTo list, read side: non-empty strings, capped, junk dropped. */
export function normalizeEventAppliesTo(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const out = value
    .filter((id): id is string => typeof id === 'string' && id.length > 0)
    .slice(0, EVENT_APPLIES_TO_MAX);
  return out.length > 0 ? out : undefined;
}

export function todayKey(): string {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Whole days from today to the given 'YYYY-MM-DD' date. Negative once the
 * date is in the past. String-only date math (midnight to midnight, local
 * time) so there's no timezone drift.
 */
export function daysUntil(date: string, from: Date = new Date()): number {
  if (!isValidCountdownDate(date)) return 0;
  const [y, m, d] = date.split('-').map(Number);
  const target = new Date(y, m - 1, d);
  const start = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  const msPerDay = 24 * 60 * 60 * 1000;
  return Math.round((target.getTime() - start.getTime()) / msPerDay);
}

/**
 * Same math but against a plain 'YYYY-MM-DD' date string instead of a Date,
 * useful for the Bag Group drop-off collapse (compare two date strings
 * without constructing a Date for "today").
 */
export function isPastDate(date: string, todayIso: string = todayKey()): boolean {
  if (!isValidCountdownDate(date)) return false;
  return date < todayIso;
}

/** Subtract N days from a 'YYYY-MM-DD' date, returning a new 'YYYY-MM-DD' string. */
export function subtractDays(date: string, days: number): string {
  if (!isValidCountdownDate(date)) return date;
  const [y, m, d] = date.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() - days);
  const yy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

/** Add N days to a 'YYYY-MM-DD' date, returning a new 'YYYY-MM-DD' string. */
export function addDays(date: string, days: number): string {
  return subtractDays(date, -days);
}

/** Whole days from one 'YYYY-MM-DD' to another. 0 when either is malformed. */
export function daysBetween(from: string, to: string): number {
  if (!isValidCountdownDate(from) || !isValidCountdownDate(to)) return 0;
  const [fy, fm, fd] = from.split('-').map(Number);
  const [ty, tm, td] = to.split('-').map(Number);
  const msPerDay = 24 * 60 * 60 * 1000;
  return Math.round(
    (new Date(ty, tm - 1, td).getTime() - new Date(fy, fm - 1, fd).getTime()) / msPerDay
  );
}

/**
 * The last day an event covers. An absent (or malformed, or backwards)
 * endDate collapses to the start date, which is exactly how every countdown
 * written before studio-wide events behaves - so a single-date event's
 * behavior is unchanged everywhere this is used.
 */
export function countdownEndDate(
  event: Pick<CountdownEventDoc, 'date' | 'endDate'>
): string {
  const end = event.endDate;
  if (!isValidCountdownDate(end) || end < event.date) return event.date;
  return end;
}

/** True when the event covers more than one day. */
export function isCountdownRange(
  event: Pick<CountdownEventDoc, 'date' | 'endDate'>
): boolean {
  return countdownEndDate(event) > event.date;
}

/** True when the given 'YYYY-MM-DD' falls inside the event, ends included. */
export function countdownCoversDate(
  event: Pick<CountdownEventDoc, 'date' | 'endDate'>,
  dateIso: string
): boolean {
  return dateIso >= event.date && dateIso <= countdownEndDate(event);
}

/**
 * "Sep 15-19" for a range inside one month, "Sep 29 - Oct 2" across months,
 * "Sep 15, 2027" for a single day. Plain hyphen on purpose: no en dashes
 * anywhere in this repo, copy or code.
 */
export function countdownDateRangeLabel(
  event: Pick<CountdownEventDoc, 'date' | 'endDate'>
): string {
  const end = countdownEndDate(event);
  if (end === event.date) return prettyCountdownDate(event.date);
  const startDate = new Date(`${event.date}T00:00:00`);
  const endDate = new Date(`${end}T00:00:00`);
  if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
    return `${event.date} - ${end}`;
  }
  const sameMonth =
    startDate.getFullYear() === endDate.getFullYear() &&
    startDate.getMonth() === endDate.getMonth();
  if (sameMonth) {
    const head = startDate.toLocaleDateString([], { month: 'short', day: 'numeric' });
    return `${head}-${endDate.getDate()}, ${endDate.getFullYear()}`;
  }
  const head = startDate.toLocaleDateString([], { month: 'short', day: 'numeric' });
  return `${head} - ${prettyCountdownDate(end)}`;
}

/** True when a multi-day event is running today, first day through last. */
export function isCountdownRunningNow(
  event: Pick<CountdownEventDoc, 'date' | 'endDate'>,
  todayIso: string = todayKey()
): boolean {
  return isCountdownRange(event) && countdownCoversDate(event, todayIso);
}

/**
 * PRESENT-TENSE COPY FOR A RUNNING RANGE, and the one place that phrasing
 * lives. Null when the event is not running, so every caller reads
 * `countdownRunningLabel(e) ?? <its own existing label>` and keeps its own
 * vocabulary for every other case.
 *
 * Why this exists (adversarial review of PR #660, P1): a range stays
 * "upcoming" through its LAST day, which is what keeps Parent Watch Week on
 * every surface all week. That makes daysUntil(START) go NEGATIVE mid-range,
 * and three family-facing counters would have rendered "in -2 days". A count
 * to a day that has already arrived is not a fact a family should be shown;
 * saying what is true right now is.
 */
export function countdownRunningLabel(
  event: Pick<CountdownEventDoc, 'date' | 'endDate'>,
  todayIso: string = todayKey()
): string | null {
  if (!isCountdownRunningNow(event, todayIso)) return null;
  return todayIso === countdownEndDate(event) ? 'Last day today' : 'Happening now';
}

/** "through Sep 19" for a running range, null otherwise. The second line
 *  under countdownRunningLabel wherever a surface has room for it. */
export function countdownThroughLabel(
  event: Pick<CountdownEventDoc, 'date' | 'endDate'>,
  todayIso: string = todayKey()
): string | null {
  if (!isCountdownRunningNow(event, todayIso)) return null;
  const end = countdownEndDate(event);
  if (todayIso === end) return null;
  const d = new Date(`${end}T00:00:00`);
  if (isNaN(d.getTime())) return `through ${end}`;
  return `through ${d.toLocaleDateString([], { month: 'short', day: 'numeric' })}`;
}

/** "today", "tomorrow", "in 5 days" - the countdown pill's own phrasing. */
export function countdownDaysLabel(days: number): string {
  if (days <= 0) return 'today';
  if (days === 1) return 'tomorrow';
  return `in ${days} days`;
}

/**
 * What a class pill says on its right: present tense while a multi-day event
 * is running, its dates while one is still ahead, the plain countdown for a
 * single date. State first, detail second - the event's NAME leads and the
 * timing follows it.
 *
 * Pure, and in the lib rather than in the component, so a plain node test can
 * import it (components/StudioEventPills.tsx is .tsx and node cannot load it).
 */
export function studioEventPillLabel(
  event: Pick<CountdownEventDoc, 'date' | 'endDate'>,
  todayIso: string = todayKey()
): string {
  if (isCountdownRange(event)) {
    // Running: present tense, never a count to a start already gone by.
    if (countdownRunningLabel(event, todayIso)) {
      return countdownThroughLabel(event, todayIso) ?? 'last day today';
    }
    return countdownDateRangeLabel(event);
  }
  return countdownDaysLabel(daysUntil(event.date, new Date(`${todayIso}T00:00:00`)));
}

/**
 * Who may see a countdown, the one place that question is answered.
 * 'everyone' shows to everyone, 'staff' only to staff, 'comp' to staff and
 * to families with a comp dancer. Lifted out of CountdownPills and
 * ImportantDates unchanged so the class pill and the calendar entries cannot
 * drift from the pills ("one thing, one place").
 */
export function countdownAudienceAllows(
  event: Pick<CountdownEventDoc, 'visibility'>,
  viewer: { isStaff: boolean; hasCompDancer?: boolean }
): boolean {
  if (event.visibility === 'everyone') return true;
  if (event.visibility === 'staff') return viewer.isStaff;
  return viewer.isStaff || !!viewer.hasCompDancer;
}

/**
 * The rsvps map, read side. Anything that is not one of the two answers is
 * dropped rather than rendered: a count is the one thing on this card a family
 * reads as a fact, so a junk value must not become a phantom "going".
 *
 * Returns undefined for an empty or absent map so the doc keeps the same shape
 * it had before this field existed.
 */
export function normalizeCompDateRsvps(
  value: unknown
): Record<string, CompDateRsvp> | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const out: Record<string, CompDateRsvp> = {};
  for (const [uid, answer] of Object.entries(value as Record<string, unknown>)) {
    if (answer === 'going' || answer === 'cant') out[uid] = answer;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

export interface NormalizedEventPagePanels {
  scheduleInfo?: string;
  hotelInfo?: string;
  travelInfo?: string;
  aboutText?: string;
}

/**
 * Normalize the four long-form event-page panels. Schedule deliberately
 * preserves an explicit empty string because it masks the legacy fallback
 * to top-level `details`; the other empty panels remain simply absent.
 */
export function normalizeEventPagePanels(value: unknown): NormalizedEventPagePanels {
  const raw = value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
  const panelText = (candidate: unknown): string | undefined => (
    typeof candidate === 'string'
    && candidate.trim()
    && candidate.length <= EVENT_PANEL_MAX
      ? candidate.trim()
      : undefined
  );
  const scheduleInfo = Object.prototype.hasOwnProperty.call(raw, 'scheduleInfo')
    && typeof raw.scheduleInfo === 'string'
    && raw.scheduleInfo.length <= EVENT_PANEL_MAX
      ? raw.scheduleInfo.trim()
      : undefined;
  const hotelInfo = panelText(raw.hotelInfo);
  const travelInfo = panelText(raw.travelInfo);
  const aboutText = panelText(raw.aboutText);
  return {
    ...(scheduleInfo !== undefined ? { scheduleInfo } : {}),
    ...(hotelInfo ? { hotelInfo } : {}),
    ...(travelInfo ? { travelInfo } : {}),
    ...(aboutText ? { aboutText } : {}),
  };
}

const EVENT_PANEL_PHOTO_ID_RE = /^[A-Za-z0-9_-]{12,80}$/;
const EVENT_PANEL_PHOTO_TYPES = new Set<EventPanelPhoto['contentType']>([
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
]);

/**
 * Normalize the server-owned panel photo map before any family browser can
 * render it. A descriptor is usable only when its Firebase URL is safe, its
 * decoded object path exactly matches storagePath, and that path belongs to
 * this exact event and panel. A valid photo from another event is still not
 * valid here.
 */
export function normalizeEventPanelPhotos(
  eventId: string,
  value: unknown
): EventPanelPhotos | undefined {
  if (
    !eventId
    || eventId !== eventId.trim()
    || eventId.length > 512
    || eventId === '.'
    || eventId === '..'
    || eventId.includes('/')
    || eventId.includes('\\')
    || /[\u0000-\u001f\u007f]/.test(eventId)
    || !value
    || typeof value !== 'object'
    || Array.isArray(value)
  ) {
    return undefined;
  }

  const raw = value as Record<string, unknown>;
  const normalized: EventPanelPhotos = {};
  for (const panel of EVENT_PANEL_PHOTO_FIELDS) {
    const candidate = raw[panel];
    if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) continue;
    const photo = candidate as Record<string, unknown>;
    if (
      Object.keys(photo).sort().join('|') !== 'contentType|id|kind|name|storagePath|url'
      || typeof photo.id !== 'string'
      || !EVENT_PANEL_PHOTO_ID_RE.test(photo.id)
      || photo.kind !== 'image'
      || typeof photo.name !== 'string'
      || !photo.name.trim()
      || photo.name.length > 160
      || typeof photo.contentType !== 'string'
      || !EVENT_PANEL_PHOTO_TYPES.has(photo.contentType as EventPanelPhoto['contentType'])
      || typeof photo.url !== 'string'
      || typeof photo.storagePath !== 'string'
      || photo.storagePath !== photo.storagePath.trim()
    ) {
      continue;
    }

    const prefix = `countdown-event-panel-attachments/${eventId}/${panel}/`;
    const remainder = photo.storagePath.startsWith(prefix)
      ? photo.storagePath.slice(prefix.length).split('/')
      : [];
    const safeUrl = safeRenderableAttachmentUrl({
      url: photo.url,
      storagePath: photo.storagePath,
    });
    if (
      !safeUrl
      || remainder.length !== 2
      || remainder.some((part) => !part)
      || !remainder[1].startsWith(`${photo.id}-`)
    ) {
      continue;
    }

    normalized[panel] = {
      id: photo.id,
      kind: 'image',
      url: safeUrl,
      name: photo.name.trim(),
      contentType: photo.contentType as EventPanelPhoto['contentType'],
      storagePath: photo.storagePath,
    };
  }
  return Object.keys(normalized).length > 0 ? normalized : undefined;
}

export function normalizeEvent(id: string, value: unknown): CountdownEventDoc {
  const raw = value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
  // One reading, shared with the server lanes (eventCore.ts).
  const visibility = normalizeEventVisibility(raw.visibility);
  const kind = normalizeEventKind(raw.kind);
  const startShowingDaysBefore = isValidCountdownWindowChoice(raw.startShowingDaysBefore)
    ? raw.startShowingDaysBefore
    : undefined;
  const details =
    typeof raw.details === 'string' && raw.details.trim() ? raw.details.trim() : undefined;
  const venue =
    typeof raw.venue === 'string' && raw.venue.trim() ? raw.venue.trim() : undefined;
  // TBD only counts when the doc carries no real date: a doc with both is a
  // dated event (a real date is never hidden behind a flag). The flag lives
  // under eventV2, same as every other v2 field, so it costs no rules shape.
  const rawV2 = raw.eventV2 && typeof raw.eventV2 === 'object' ? (raw.eventV2 as Record<string, unknown>) : {};
  const dateTbd = rawV2.dateTbd === true && !isValidCountdownDate(raw.date);
  const date = dateTbd ? '' : isValidCountdownDate(raw.date) ? raw.date : todayKey();
  // A malformed or backwards endDate is dropped rather than trusted, so the
  // doc reads as the single-day event it effectively is instead of rendering
  // a range that runs backwards.
  const endDate =
    !dateTbd && isValidCountdownDate(raw.endDate) && raw.endDate >= date ? raw.endDate : undefined;
  const rsvps = normalizeCompDateRsvps(raw.rsvps);
  // EVENT MODEL V2 LIVES UNDER ONE eventV2 MAP ON THE WIRE (Stage B of the
  // 2026-08-20 release-cliff incident): nineteen sibling key allowances
  // cost the rules budget nineteen shape gates plus nineteen key-list
  // entries and helped tip the compiled ruleset over Firebase's
  // release-activation ceiling; one map key costs one `is map` gate. The
  // NORMALIZED form stays flat - only this read boundary and the two write
  // boundaries (useCreateCountdownEvent, updateCountdownEvent) know about
  // the map. No production doc ever carried the flat v2 fields (their
  // writes were rejected for the whole outage window), so there is no
  // legacy flat read here on purpose.
  //
  // Every invalid value is DROPPED rather than trusted, so a malformed doc
  // reads exactly like a pre-v2 doc and every consumer keeps its
  // absent-safe default.
  const v2 = raw.eventV2 && typeof raw.eventV2 === 'object'
    ? (raw.eventV2 as Record<string, unknown>)
    : {};
  const eventType = (EVENT_TYPE_CHOICES as readonly unknown[]).includes(v2.eventType)
    ? (v2.eventType as EventTypeChoice)
    : undefined;
  const customTypeId =
    eventType === 'custom' && typeof v2.customTypeId === 'string' && v2.customTypeId
      ? v2.customTypeId
      : undefined;
  const startTime = isValidEventTime(v2.startTime) ? v2.startTime : undefined;
  const endTime = isValidEventTime(v2.endTime) ? v2.endTime : undefined;
  const arriveMinutesBefore = isValidArriveMinutesBefore(v2.arriveMinutesBefore)
    ? v2.arriveMinutesBefore
    : undefined;
  const callTimes = normalizeEventCallTimes(v2.callTimes);
  const appliesTo = normalizeEventAppliesTo(v2.appliesTo);
  const amountDueNote =
    typeof v2.amountDueNote === 'string'
    && v2.amountDueNote.trim()
    && v2.amountDueNote.length <= EVENT_AMOUNT_DUE_MAX
      ? v2.amountDueNote.trim()
      : undefined;
  const feeDueDate = isValidCountdownDate(v2.feeDueDate) ? v2.feeDueDate : undefined;
  const { scheduleInfo, hotelInfo, travelInfo, aboutText } = normalizeEventPagePanels(v2);
  const panelPhotos = normalizeEventPanelPhotos(id, v2.panelPhotos);
  const about = normalizeEventAbout(v2.about);
  const unpublished = Array.isArray(v2.unpublished)
    ? EVENT_PUBLISH_KEYS.filter((key) => (v2.unpublished as unknown[]).includes(key))
    : [];
  const pillScope = (EVENT_PILL_SCOPES as readonly unknown[]).includes(v2.pillScope)
    ? (v2.pillScope as EventPillScope)
    : undefined;
  const pageKind = (EVENT_PAGE_KINDS as readonly unknown[]).includes(v2.pageKind)
    ? (v2.pageKind as EventPageKind)
    : undefined;
  const slug = isValidEventSlug(v2.slug) ? v2.slug : undefined;
  const repeat = (EVENT_REPEAT_CHOICES as readonly unknown[]).includes(v2.repeat)
    ? (v2.repeat as EventRepeatChoice)
    : undefined;
  const repeatUntil =
    repeat && isValidCountdownDate(v2.repeatUntil) && v2.repeatUntil >= date
      ? v2.repeatUntil
      : undefined;
  const parentEventId =
    typeof v2.parentEventId === 'string' && v2.parentEventId && v2.parentEventId !== id
      ? v2.parentEventId
      : undefined;
  const countdownHeadline =
    typeof v2.countdownHeadline === 'string'
    && v2.countdownHeadline.trim()
    && v2.countdownHeadline.length <= EVENT_COUNTDOWN_HEADLINE_MAX
      ? v2.countdownHeadline.trim()
      : undefined;
  const countdownTarget = (EVENT_COUNTDOWN_TARGETS as readonly unknown[]).includes(
    v2.countdownTarget
  )
    ? (v2.countdownTarget as EventCountdownTarget)
    : undefined;
  const countdownUnits = (EVENT_COUNTDOWN_UNITS as readonly unknown[]).includes(
    v2.countdownUnits
  )
    ? (v2.countdownUnits as EventCountdownUnits)
    : undefined;
  return {
    id,
    name: typeof raw.name === 'string' && raw.name.trim() ? raw.name.trim() : 'Countdown',
    emoji: typeof raw.emoji === 'string' && raw.emoji ? raw.emoji : '🏆',
    color: isValidHexColor(raw.color) ? raw.color : '#A8E63A',
    date,
    ...(dateTbd ? { dateTbd: true as const } : {}),
    ...(endDate ? { endDate } : {}),
    ...(raw.showOnClasses === true ? { showOnClasses: true as const } : {}),
    visibility,
    kind,
    ...(startShowingDaysBefore != null ? { startShowingDaysBefore } : {}),
    ...(details ? { details } : {}),
    ...(venue ? { venue } : {}),
    ...(rsvps ? { rsvps } : {}),
    ...(eventType ? { eventType } : {}),
    ...(customTypeId ? { customTypeId } : {}),
    ...(startTime ? { startTime } : {}),
    ...(endTime ? { endTime } : {}),
    ...(typeof v2.countdownEnabled === 'boolean' ? { countdownEnabled: v2.countdownEnabled } : {}),
    ...(v2.allDay === true ? { allDay: true as const } : {}),
    ...(arriveMinutesBefore != null ? { arriveMinutesBefore } : {}),
    ...(callTimes ? { callTimes } : {}),
    ...(appliesTo ? { appliesTo } : {}),
    ...(amountDueNote ? { amountDueNote } : {}),
    ...(feeDueDate ? { feeDueDate } : {}),
    ...(scheduleInfo !== undefined ? { scheduleInfo } : {}),
    ...(hotelInfo ? { hotelInfo } : {}),
    ...(travelInfo ? { travelInfo } : {}),
    ...(aboutText ? { aboutText } : {}),
    ...(panelPhotos ? { panelPhotos } : {}),
    ...(about ? { about } : {}),
    ...(unpublished.length > 0 ? { unpublished } : {}),
    ...(v2.rsvpRequired === true ? { rsvpRequired: true as const } : {}),
    ...(pillScope ? { pillScope } : {}),
    ...(pageKind ? { pageKind } : {}),
    ...(slug ? { slug } : {}),
    ...(repeat && !dateTbd ? { repeat } : {}),
    ...(repeatUntil && !dateTbd ? { repeatUntil } : {}),
    ...(parentEventId ? { parentEventId } : {}),
    ...(countdownHeadline ? { countdownHeadline } : {}),
    ...(countdownTarget ? { countdownTarget } : {}),
    ...(countdownUnits ? { countdownUnits } : {}),
    createdAt: raw.createdAt as Timestamp | FieldValue,
    createdBy: typeof raw.createdBy === 'string' ? raw.createdBy : '',
    updatedAt: raw.updatedAt as Timestamp | FieldValue,
  };
}

/**
 * Whether a countdown should render as a PILL today, per its display
 * window. Absent startShowingDaysBefore means always-show - the behavior
 * every countdown had before this field existed, so no existing pill
 * changes. Boundary is inclusive: exactly N days out shows, N+1 does not
 * (today >= date minus the window).
 */
export function isCountdownWindowVisible(
  event: Pick<CountdownEventDoc, 'date' | 'startShowingDaysBefore'>,
  todayIso: string = todayKey()
): boolean {
  if (event.startShowingDaysBefore == null) return true;
  return todayIso >= subtractDays(event.date, event.startShowingDaysBefore);
}

/** The 'YYYY-MM-DD' date a countdown starts showing, or null if it always shows. */
export function countdownStartShowingDate(
  event: Pick<CountdownEventDoc, 'date' | 'startShowingDaysBefore'>
): string | null {
  if (event.startShowingDaysBefore == null) return null;
  return subtractDays(event.date, event.startShowingDaysBefore);
}

/** What every surface says in place of a date for a TBD event. */
export const COUNTDOWN_DATE_TBD_LABEL = 'Date to be confirmed';

/** The date line for one event: the pretty date, or the TBD words. Never
 * "Invalid Date", because a TBD event has no date to format. */
export function countdownDateLabel(event: { dateTbd?: boolean; date: string }): string {
  return event.dateTbd === true || !isValidCountdownDate(event.date)
    ? COUNTDOWN_DATE_TBD_LABEL
    : prettyCountdownDate(event.date);
}

/** Only the events that have a real date. A belt over the live query, which
 * already leaves TBD docs out because it orders by `date`. */
export function datedCountdownEvents<T extends { dateTbd?: boolean }>(events: T[]): T[] {
  return events.filter((event) => event.dateTbd !== true);
}

/** The TBD events one viewer may see, A to Z (they have no date to sort by,
 * so they sit in their own list after every dated row). */
export function tbdCountdownEventsForViewer(
  events: CountdownEventDoc[],
  viewer: { isStaff: boolean; hasCompDancer?: boolean }
): CountdownEventDoc[] {
  return events
    .filter((event) => event.dateTbd === true && countdownAudienceAllows(event, viewer))
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
}

/** Compact human date for list rows and honest "starts showing" notes, e.g. "Jun 14, 2027". */
export function prettyCountdownDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
}

export interface UseCountdownEventsResult {
  loading: boolean;
  events: CountdownEventDoc[];
  error: string | null;
}

/** Subscribe to every countdown event, ordered by date ascending. */
export function useCountdownEvents(): UseCountdownEventsResult {
  const [events, setEvents] = useState<CountdownEventDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!db) {
      setError('Firestore not initialized.');
      setLoading(false);
      return;
    }
    const q = query(collection(db, 'countdownEvents'), orderBy('date', 'asc'));
    const unsub = onSnapshot(
      q,
      // includeMetadataChanges is REQUIRED for the fromCache gate below, not a
      // nicety: without it a cache-empty-then-server-empty studio fires only
      // the cache event, because the server confirmation changes metadata
      // alone and is suppressed, and loading would hang forever. Same shape as
      // useDayReservations, which documents this at length.
      { includeMetadataChanges: true },
      (snap) => {
        // An empty list Firestore says came from CACHE is "we have not heard
        // from the server yet", not "the studio has no events". Rendering it
        // as an answer is how a cold mount tells a comp family "No
        // competitions are listed yet" while nationals is three weeks out.
        if (snap.empty && snap.metadata.fromCache) return;
        setEvents(datedCountdownEvents(snap.docs.map((d) => normalizeEvent(d.id, d.data()))));
        setError(null);
        setLoading(false);
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      }
    );
    return () => unsub();
  }, []);

  return { loading, events, error };
}

/**
 * The "Date to be confirmed" events, and ONLY those. They carry no `date`, so
 * the live list above (ordered by `date`) cannot return them; this is the one
 * separate door. A single-field equality on a map key needs no composite
 * index. Callers sort and audience-filter with tbdCountdownEventsForViewer.
 */
export function useTbdCountdownEvents(): UseCountdownEventsResult {
  const [events, setEvents] = useState<CountdownEventDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!db) {
      setError('Firestore not initialized.');
      setLoading(false);
      return;
    }
    const q = query(collection(db, 'countdownEvents'), where('eventV2.dateTbd', '==', true));
    const unsub = onSnapshot(
      q,
      (snap) => {
        setEvents(
          snap.docs.map((d) => normalizeEvent(d.id, d.data())).filter((event) => event.dateTbd === true)
        );
        setError(null);
        setLoading(false);
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      }
    );
    return () => unsub();
  }, []);

  return { loading, events, error };
}

/**
 * Countdown events that have not finished yet, earliest first (already the
 * query order). A single-date event finishes on its own date, which is the
 * pre-range behavior unchanged; a range stays "upcoming" through its LAST
 * day, so Parent Watch Week does not vanish from every surface on the
 * Tuesday of the week it is running.
 */
export function upcomingCountdownEvents(
  events: CountdownEventDoc[],
  todayIso: string = todayKey()
): CountdownEventDoc[] {
  return events.filter((event) => countdownEndDate(event) >= todayIso);
}

/**
 * Countdown events safe to show as a PILL today: not yet passed AND inside
 * their display window (packet 24). Every pill surface (CountdownPills,
 * Landing, CommandCenter, FamilyCalendar's "coming up" preview) should
 * filter through this instead of calling upcomingCountdownEvents directly.
 * The management list under Calendar tools keeps reading the raw events
 * list on purpose, so a not-yet-showing countdown stays visible there with
 * its "starts showing <date>" note instead of disappearing.
 */
export function windowVisibleCountdownEvents(
  events: CountdownEventDoc[],
  todayIso: string = todayKey()
): CountdownEventDoc[] {
  return upcomingCountdownEvents(events, todayIso).filter((event) =>
    isCountdownWindowVisible(event, todayIso)
  );
}

/**
 * The HOME-SCREEN countdown surfaces (CountdownPills, the staff home ticker)
 * on top of the window rule above. An event saved with "No countdown"
 * (eventV2.countdownEnabled === false) keeps its calendar entry, its
 * Important dates row, its class pills and its place on the Comp Checklist;
 * it only stops counting down on home screens. The dancer app applies the
 * same rule in dancerEventPills.homePillEvents. Refute finding on PR #945:
 * folding this into windowVisibleCountdownEvents made FamilyCalendar and
 * CompChecklist drop the display window instead.
 */
export function homeCountdownEvents(
  events: CountdownEventDoc[],
  todayIso: string = todayKey()
): CountdownEventDoc[] {
  return windowVisibleCountdownEvents(events, todayIso).filter((event) => event.countdownEnabled !== false);
}

export interface CountdownViewer {
  isStaff: boolean;
  hasCompDancer?: boolean;
}

/**
 * The events that render as a pill on a class, for one specific day.
 *
 * Bobbie's ask is literal: "parent watch week needs to show up as a pill on
 * all classes DURING that week", so the pill is on from the first day of the
 * range through the last, and off outside it. Two deliberate calls the spec
 * did not make, both written down rather than guessed at silently:
 *
 *  - showOnClasses must be explicitly true. An ordinary countdown (Nationals,
 *    a recital) does NOT start appearing on class pages just because ranges
 *    now exist. Absent means false, so nothing already in the studio's list
 *    changes.
 *  - the display WINDOW is an early door only. With no window set the pill
 *    shows exactly during the range (otherwise an always-show event would sit
 *    on every class page for months, which is not "during that week"). With a
 *    window set, the pill also appears that many days ahead, because that is
 *    what the "Start showing" control on the editor promises.
 *
 * `dateIso` is the day being asked about: today on a class page, the meeting's
 * own date on a dated class card.
 */
export function classPillCountdownEvents(
  events: CountdownEventDoc[],
  viewer: CountdownViewer,
  dateIso: string = todayKey()
): CountdownEventDoc[] {
  return events.filter((event) => {
    if (event.showOnClasses !== true) return false;
    if (!countdownAudienceAllows(event, viewer)) return false;
    if (dateIso > countdownEndDate(event)) return false;
    if (dateIso >= event.date) return true;
    return event.startShowingDaysBefore != null && isCountdownWindowVisible(event, dateIso);
  });
}

/**
 * The events that render as entries on a calendar day. Every countdown the
 * viewer may see whose range covers that day, ordered earliest start first.
 *
 * The display window is DELIBERATELY not applied here, for the same reason
 * the Important Dates view ignores it: a window gates a PILL, and a calendar
 * is a dated view - an event on Sep 15 belongs on Sep 15 whether or not its
 * home-screen pill has started showing yet.
 */
export function calendarCountdownEventsForDate(
  events: CountdownEventDoc[],
  viewer: CountdownViewer,
  dateIso: string
): CountdownEventDoc[] {
  return events
    .filter(
      (event) =>
        countdownAudienceAllows(event, viewer) && countdownCoversDate(event, dateIso)
    )
    .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
}

/**
 * Every 'YYYY-MM-DD' inside a window that carries at least one calendar
 * entry, so a month grid can mark its cells without re-filtering per cell.
 * Capped at a year of days so a typo in an endDate cannot spin here.
 */
export function countdownDatesInWindow(
  events: CountdownEventDoc[],
  viewer: CountdownViewer,
  fromIso: string,
  toIso: string
): Set<string> {
  const marked = new Set<string>();
  for (const event of events) {
    if (!countdownAudienceAllows(event, viewer)) continue;
    const end = countdownEndDate(event);
    if (end < fromIso || event.date > toIso) continue;
    let cursor = event.date > fromIso ? event.date : fromIso;
    const last = end < toIso ? end : toIso;
    for (let guard = 0; guard <= 366 && cursor <= last; guard += 1) {
      marked.add(cursor);
      cursor = addDays(cursor, 1);
    }
  }
  return marked;
}

/** Just the competition-kind events, for the Bag Group picker. */
export function useCompetitionCountdownEvents(): UseCountdownEventsResult {
  const { events, loading, error } = useCountdownEvents();
  const competitions = useMemo(() => events.filter((e) => e.kind === 'competition'), [events]);
  return { loading, events: competitions, error };
}

/** The event model v2 fields a writer may set. Undefined everywhere = a
 * pre-v2 date, byte for byte. See CountdownEventDoc for field meaning. */
export interface EventModelV2Input {
  countdownEnabled?: boolean;
  eventType?: EventTypeChoice;
  customTypeId?: string;
  startTime?: string;
  endTime?: string;
  allDay?: boolean;
  arriveMinutesBefore?: number;
  callTimes?: Record<string, string>;
  appliesTo?: string[];
  amountDueNote?: string;
  feeDueDate?: string;
  scheduleInfo?: string;
  hotelInfo?: string;
  travelInfo?: string;
  aboutText?: string;
  /** The About tab's smart editor draft. Always sent whole (never a partial
   * patch) - see eventAbout.ts's validatedEventAboutInput. */
  about?: EventAboutInput;
  rsvpRequired?: boolean;
  pillScope?: EventPillScope;
  pageKind?: EventPageKind;
  slug?: string;
  repeat?: EventRepeatChoice;
  repeatUntil?: string;
  parentEventId?: string;
  countdownHeadline?: string;
  countdownTarget?: EventCountdownTarget;
  countdownUnits?: EventCountdownUnits;
}

/**
 * Validate the v2 fields of a draft and return exactly the keys to write.
 * Shared by create and update so both doors throw the same words, the same
 * contract COUNTDOWN_END_BEFORE_START records. Only DEFINED, non-empty
 * values come back; the caller decides whether an absent key means "leave
 * it" (create) or "delete it" (update, via v2FieldsToClear).
 *
 * The cross-field laws, mirrored in shared/firestore.rules where cheap and
 * enforced only here where a rules clause would buy little (time ordering,
 * documented there):
 *  - customTypeId only rides with eventType 'custom'
 *  - slug only rides with pageKind, and must be URL-shaped
 *  - repeatUntil only rides with repeat, and never lands before `date`
 *  - endTime on a single-day event must be after startTime
 */
export function validatedEventModelV2Fields(
  input: EventModelV2Input,
  context: { date: string; endDate?: string }
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (input.countdownEnabled !== undefined) {
    if (typeof input.countdownEnabled !== 'boolean') throw new Error('Choose whether to show a countdown.');
    out.countdownEnabled = input.countdownEnabled;
  }
  if (input.eventType !== undefined) {
    if (!(EVENT_TYPE_CHOICES as readonly string[]).includes(input.eventType)) {
      throw new Error('Pick one of the event types.');
    }
    out.eventType = input.eventType;
  }
  if (input.customTypeId !== undefined && input.customTypeId !== '') {
    if (out.eventType !== 'custom') {
      throw new Error('A custom type id only goes with the Custom event type.');
    }
    out.customTypeId = input.customTypeId;
  }
  const startTime = (input.startTime ?? '').trim();
  if (startTime) {
    if (!isValidEventTime(startTime)) throw new Error('Times are HH:MM, like 17:30.');
    out.startTime = startTime;
  }
  const endTime = (input.endTime ?? '').trim();
  if (endTime) {
    if (!isValidEventTime(endTime)) throw new Error('Times are HH:MM, like 17:30.');
    const singleDay = !context.endDate || context.endDate === context.date;
    if (singleDay && startTime && endTime <= startTime) {
      throw new Error('The end time must be after the start time.');
    }
    out.endTime = endTime;
  }
  if (input.allDay === true) out.allDay = true;
  if (input.arriveMinutesBefore !== undefined) {
    if (!isValidArriveMinutesBefore(input.arriveMinutesBefore)) {
      throw new Error(
        `Arrive-before is whole minutes, up to ${EVENT_ARRIVE_BEFORE_MAX_MINUTES}.`
      );
    }
    if (input.arriveMinutesBefore > 0) out.arriveMinutesBefore = input.arriveMinutesBefore;
  }
  if (input.callTimes !== undefined) {
    const entries = Object.entries(input.callTimes);
    if (entries.length > EVENT_APPLIES_TO_MAX) {
      throw new Error(`Call times cover up to ${EVENT_APPLIES_TO_MAX} classes.`);
    }
    for (const [offeringId, time] of entries) {
      if (!offeringId || !isValidEventTime(time)) {
        throw new Error('Every call time needs a class and an HH:MM time.');
      }
    }
    if (entries.length > 0) out.callTimes = input.callTimes;
  }
  if (input.appliesTo !== undefined) {
    if (input.appliesTo.length > EVENT_APPLIES_TO_MAX) {
      throw new Error(`A date applies to up to ${EVENT_APPLIES_TO_MAX} classes.`);
    }
    const ids = input.appliesTo.filter((id) => typeof id === 'string' && id);
    if (ids.length !== input.appliesTo.length) {
      throw new Error('Every applies-to entry needs a class.');
    }
    if (ids.length > 0) out.appliesTo = ids;
  }
  const amountDueNote = (input.amountDueNote ?? '').trim();
  if (amountDueNote) {
    if (amountDueNote.length > EVENT_AMOUNT_DUE_MAX) {
      throw new Error(`Keep the amount-due line under ${EVENT_AMOUNT_DUE_MAX} characters.`);
    }
    out.amountDueNote = amountDueNote;
  }
  const feeDueDate = (input.feeDueDate ?? '').trim();
  if (feeDueDate) {
    if (!isValidCountdownDate(feeDueDate)) throw new Error('Pick a valid fee due date.');
    out.feeDueDate = feeDueDate;
  }
  // The four event-page panels. `in`-checked rather than truthy-checked,
  // because CLEARING one is a real edit a staff member makes: passing an
  // empty string must be able to REMOVE the panel, and a truthy test would
  // silently keep the old text on screen while telling them it saved. An
  // absent key is left alone, so a caller sending only `hotelInfo` does not
  // wipe travel and about.
  // Schedule alone preserves an explicit empty string. It used to fall back
  // to top-level details, so deleting the subkey on clear would resurrect
  // the old schedule. Keeping the empty override leaves details available to
  // Important Dates, search and calendar feeds without lying on this page.
  if ('scheduleInfo' in input) {
    const text = (input.scheduleInfo ?? '').trim();
    if (text.length > EVENT_PANEL_MAX) {
      throw new Error(
        `Keep this event panel under ${EVENT_PANEL_MAX.toLocaleString('en-US')} characters.`
      );
    }
    out.scheduleInfo = text;
  }
  for (const key of ['hotelInfo', 'travelInfo', 'aboutText'] as const) {
    if (!(key in input)) continue;
    const text = (input[key] ?? '').trim();
    if (text.length > EVENT_PANEL_MAX) {
      throw new Error(
        `Keep this event panel under ${EVENT_PANEL_MAX.toLocaleString('en-US')} characters.`
      );
    }
    if (text) out[key] = text;
  }
  // Whole-object write, never merged field by field: the editor always
  // sends its full section list, the same "Save" semantics compBoard's post
  // composer keeps for its own draft. An absent `about` key leaves the
  // stored map untouched, matching every other v2 field's absent-means-
  // unchanged contract.
  if (input.about !== undefined) {
    out.about = validatedEventAboutInput(input.about);
  }
  if (input.rsvpRequired === true) out.rsvpRequired = true;
  if (input.pillScope !== undefined) {
    if (!(EVENT_PILL_SCOPES as readonly string[]).includes(input.pillScope)) {
      throw new Error('The pill shows on all classes or the tagged ones.');
    }
    out.pillScope = input.pillScope;
  }
  if (input.pageKind !== undefined) {
    if (!(EVENT_PAGE_KINDS as readonly string[]).includes(input.pageKind)) {
      throw new Error('Pick one of the page kinds.');
    }
    out.pageKind = input.pageKind;
  }
  const slug = (input.slug ?? '').trim();
  if (slug) {
    if (out.pageKind === undefined) {
      throw new Error('A page link needs a page: pick the page kind first.');
    }
    if (!isValidEventSlug(slug)) {
      throw new Error('Page links are lowercase letters, numbers, and hyphens, 3 to 80 long.');
    }
    out.slug = slug;
  }
  if (input.repeat !== undefined) {
    if (!(EVENT_REPEAT_CHOICES as readonly string[]).includes(input.repeat)) {
      throw new Error('Pick one of the repeat choices.');
    }
    out.repeat = input.repeat;
  }
  const repeatUntil = (input.repeatUntil ?? '').trim();
  if (repeatUntil) {
    if (out.repeat === undefined) {
      throw new Error('A repeat-until date needs a repeat choice first.');
    }
    if (!isValidCountdownDate(repeatUntil) || repeatUntil < context.date) {
      throw new Error('Repeat-until is a date on or after the start date.');
    }
    out.repeatUntil = repeatUntil;
  }
  const parentEventId = (input.parentEventId ?? '').trim();
  if (parentEventId) out.parentEventId = parentEventId;
  const countdownHeadline = (input.countdownHeadline ?? '').trim();
  if (countdownHeadline) {
    if (countdownHeadline.length > EVENT_COUNTDOWN_HEADLINE_MAX) {
      throw new Error(
        `Keep the countdown headline under ${EVENT_COUNTDOWN_HEADLINE_MAX} characters.`
      );
    }
    out.countdownHeadline = countdownHeadline;
  }
  if (input.countdownTarget !== undefined) {
    if (!(EVENT_COUNTDOWN_TARGETS as readonly string[]).includes(input.countdownTarget)) {
      throw new Error('The countdown counts to the start or the call.');
    }
    out.countdownTarget = input.countdownTarget;
  }
  if (input.countdownUnits !== undefined) {
    if (!(EVENT_COUNTDOWN_UNITS as readonly string[]).includes(input.countdownUnits)) {
      throw new Error('The countdown shows days, or days and hours.');
    }
    out.countdownUnits = input.countdownUnits;
  }
  return out;
}

/** Every v2 field name, in one place, for the update door's clear pass. */
export const EVENT_MODEL_V2_FIELDS = [
  'countdownEnabled',
  'eventType',
  'customTypeId',
  'startTime',
  'endTime',
  'allDay',
  'arriveMinutesBefore',
  'callTimes',
  'appliesTo',
  'amountDueNote',
  'feeDueDate',
  'scheduleInfo',
  'hotelInfo',
  'travelInfo',
  'aboutText',
  'about',
  'rsvpRequired',
  'pillScope',
  'pageKind',
  'slug',
  'repeat',
  'repeatUntil',
  'parentEventId',
  'countdownHeadline',
  'countdownTarget',
  'countdownUnits',
] as const;

export interface NewCountdownEventInput extends EventModelV2Input {
  name: string;
  emoji: string;
  color: string;
  date: string;
  /** Empty or undefined = a single-day event. Never earlier than `date`. */
  endDate?: string;
  /** True = "Date to be confirmed": saved with NO date and NO end date
   * (`date` is ignored), and a repeat is dropped, since a repeat needs a start. */
  dateTbd?: boolean;
  /** Undefined or false = no pill on class pages. */
  showOnClasses?: boolean;
  visibility: CountdownVisibility;
  kind: CountdownKind;
  /** Absent or undefined = always show the pill. */
  startShowingDaysBefore?: CountdownWindowChoice;
  details?: string;
  /** One typed "venue, city" line; empty or undefined = none entered. */
  venue?: string;
}

/**
 * The one message a person sees when the end date is before the start.
 * Shared by create and update so both doors say the same thing, and by the
 * editor so the button is disabled before anyone can try.
 */
export const COUNTDOWN_END_BEFORE_START =
  'The end date cannot be before the start date.';

/** How the notify step reports back. Never throws; see reportCountdownEventNotify. */
export type CountdownEventNotifyReport = (
  result: NotifyDeliveryResult | null,
  detail?: string
) => void;

/** A delivery-status renderer cannot change an already-saved event's outcome. */
function reportCountdownEventOutcome(
  onNotify: CountdownEventNotifyReport | undefined,
  result: NotifyDeliveryResult | null,
  detail?: string
): void {
  try {
    onNotify?.(result, detail);
  } catch {
    // Reporting is optional. Its failure must not turn a saved event into
    // a failed save, call the reporter twice, or retry notification delivery.
  }
}

/**
 * WHO HEARS ABOUT A NEW IMPORTANT DATE, resolved from the event's own
 * visibility - the same three-way test countdownAudienceAllows() uses to
 * decide who may SEE it, so nobody is ever told about a date they then
 * cannot open.
 *
 * Reads `users`, which is why this is only ever called from the create
 * path: every door onto that path is behind canManageCountdowns() (see
 * roleCapabilities.ts), so the caller is always staff and always allowed
 * to list users. A family account structurally cannot reach this.
 *
 *   - 'everyone'  -> every active account
 *   - 'staff'     -> active staff accounts only
 *   - comp (default) -> the comp roll UNION active staff, THEN the Manage
 *     members overlay applied on top (added uids in, removed uids out) -
 *     matching countdownAudienceAllows' `viewer.isStaff || viewer.hasCompDancer`,
 *     which CompBoard.tsx and CompetitionEvent.tsx both feed from the same
 *     overlay-composed `member`, never the roll alone. The roll comes from
 *     compBoardMembership/current, the same document the comp board's own
 *     post lane seeds from; the overlay from compBoardMembershipOverlay/current.
 *
 * Never throws: a failed read degrades to the smaller, always-correct staff
 * audience rather than turning a saved date into an error in the composer.
 */
/**
 * Codex round 7 on #805, finding 2: thrown ONLY for a failed Manage members
 * overlay read, so a caller can tell "confirmed nobody to alert" (an empty
 * array) apart from "could not confirm who is still really on the board"
 * (this rejecting) - see this function's own overlay-read paragraph below
 * for why that distinction is the whole point of the class existing.
 */
export class CountdownAudienceUnconfirmedError extends Error {}

async function countdownEventRecipients(
  visibility: CountdownVisibility
): Promise<string[]> {
  if (!db) return [];
  const staffRoles: UserRole[] = ['director', 'manager', 'company', 'ensemble', 'foh'];
  let users: Array<{ uid: string; role: UserRole; canary?: boolean }>;
  try {
    const snap = await getDocs(query(collection(db, 'users'), where('status', '==', 'active')));
    users = snap.docs.map((d) => d.data() as { uid: string; role: UserRole; canary?: boolean });
  } catch {
    // The audience shape itself could not be read at all. Degrades to the
    // smaller, always-correct empty answer rather than turning a saved
    // date into a thrown error in the composer - unchanged from before this
    // round; this is a different read than the overlay below and carries
    // none of the risk that read's own comment documents.
    return [];
  }
  if (visibility === 'everyone') return audienceUidsFromActiveUsers(users, []);
  const staffUids = audienceUidsFromActiveUsers(users, staffRoles);
  if (visibility === 'staff') return staffUids;
  const uids = new Set(staffUids);
  try {
    const membership = await getDoc(doc(db, 'compBoardMembership', 'current'));
    const memberUids = (membership.data() ?? {}).memberUids;
    if (Array.isArray(memberUids)) {
      memberUids.forEach((uid) => typeof uid === 'string' && uid && uids.add(uid));
    }
  } catch {
    // The roll could not be read. Staff still hear about it; caught and
    // degraded on purpose, unlike the overlay just below: an unreadable
    // ROLL only means fewer names ever get ADDED to the set, never a
    // removed uid staying in it, so it carries none of the risk the
    // overlay's own comment documents - the same ADD-side-is-safe-to-fail-
    // open reasoning readBoardMembershipOverlay's server-side comment
    // documents for the identical read there.
  }
  // THE OVERLAY (Codex round 6 on #805, finding 1): the roll above is only
  // half of who is actually on the comp board - Manage members can ADD a
  // family the roll does not carry (they get no notice of a new comp date
  // without this) or REMOVE one that is still on it (they would otherwise
  // be told about a date, then hit a board they can no longer open). Same
  // overlay doc and field names isEffectiveBoardMember() composes
  // everywhere else, applied here as a set op since this is a batch of
  // uids rather than one uid's own membership question.
  //
  // FAIL CLOSED (Codex round 7 on #805, finding 2). The round 6 shape wrapped
  // this read in its own try/catch and degraded to the roll-plus-staff
  // answer above on failure - which means a DIRECTOR-REMOVED member
  // received the date alert exactly when this read failed, the one moment
  // nobody could have caught it, because unlike a UI fallback this SENDS. A
  // missed alert is retryable from the composer; one already delivered to a
  // removed member is not. So this is deliberately NOT wrapped in try/catch
  // here: a rejection propagates as CountdownAudienceUnconfirmedError,
  // straight out of this function, and both callers below already hold the
  // exact "the read failed, tell nobody rather than guess" shape for their
  // own doc re-read (see 'could not read the date back to see who to
  // tell') - this rejection lands in that SAME onNotify(null, ...) path,
  // never a silent roll-plus-staff send.
  let overlay;
  try {
    overlay = await getDoc(doc(db, OVERLAY_COLLECTION.compBoard, 'current'));
  } catch (e) {
    throw new CountdownAudienceUnconfirmedError(
      e instanceof Error ? e.message : String(e)
    );
  }
  const data = overlayRecord(overlay.data());
  overlayStringList(data.addedUids).forEach((uid) => uids.add(uid));
  overlayStringList(data.removedUids).forEach((uid) => uids.delete(uid));
  return [...uids];
}

/**
 * Fires exactly once, always, and never throws - the same contract
 * classPosts.ts's reportClassPostNotify and compBoard.ts's
 * reportCompBoardNotify already hold, and for the same reason: the event is
 * already saved by the time this runs, so a notify failure is not a failed
 * save, but it is not a success either and the composer has to be able to
 * say which.
 */
async function reportCountdownEventNotify(
  recipientUids: string[],
  payload: NotifyPayload,
  onNotify?: CountdownEventNotifyReport,
  countdownEventId?: string
): Promise<void> {
  // COMP-VISIBILITY DATES NOW CARRY A REAL, BOARD-SCOPED AUTHORITY (Codex
  // round 7 on #805, finding 1). Every other visibility ('everyone',
  // 'staff') still sends with none, same as before this round, and still
  // rides notify.mjs's 'unscoped' studio lane on the same STUDIO_ROLES-only
  // reasoning the paragraph below always documented - neither of those
  // visibilities has a board to mute in the first place.
  //
  // A comp date carrying countdownEventId, though, now sends
  // { compBoardCountdown: { id: countdownEventId } } (see both call sites
  // below): the server keys isBoardNotificationMuted() off the SAME
  // 'compBoard' lane a post already resolves to, so a member who muted
  // "the Comp Team board" stops hearing about its dates too, on every
  // channel, not only the bell - see notificationAuthority.mjs's own
  // comment on that new branch for the full shape. THIS was the hidden
  // dependency the paragraph below used to warn about for every visibility;
  // it is now closed for the one visibility that actually needed it.
  //
  // WIDEN canManageCountdowns TO 'ensemble' AND THE UNSCOPED SEND GOES
  // SILENT for 'everyone'/'staff' dates: no error, no log, the date just
  // tells nobody - exactly how a Jr Staff teacher's class posts came to
  // notify nobody for months (that diagnosis was archived on 2026-08-27
  // once the fix was confirmed shipped in #715; it is now
  // docs/archive/queue-shipped-2026-08-27-jr-staff-class-alerts-diagnosis.md, which
  // carries "A JR STAFF TEACHER'S CLASS POSTS NOTIFY NOBODY" verbatim). If
  // that widening ever happens, those two visibilities need a real
  // authority lane too, same as comp just got.
  if (recipientUids.length === 0) {
    reportCountdownEventOutcome(onNotify, null, 'nobody to alert about this date');
    return;
  }
  try {
    // Every save caller sends, including editors without a delivery-status
    // callback. Optional-call arguments would skip this await entirely.
    const result = await notifyUsers(
      recipientUids,
      payload,
      countdownEventId ? { compBoardCountdown: { id: countdownEventId } } : {}
    );
    reportCountdownEventOutcome(onNotify, result);
  } catch (e) {
    reportCountdownEventOutcome(onNotify, null, e instanceof Error ? e.message : String(e));
  }
}

/**
 * THE ALERT AN IMPORTANT DATE SENDS WHEN IT IS CREATED.
 *
 * Bobbie, 2026-08-26, urgent: *"would it be possible to add an alert or
 * notification to the comp team board? Many parents were shocked that items
 * had been placed there and didn't know it was a thing."* then *"All items
 * on the comp board must send a notification!!"*
 *
 * Every comp date families see on the comp board's DATES tab is one of these
 * documents, and creating one used to tell nobody at all - not the bell, not
 * a phone. That is the whole reason a parent could find a date on the board
 * they had never heard of. The comp board's POSTS already notified (PR #692,
 * #694); its dates never did.
 *
 * The copy names the date and points at it, and carries no detail beyond the
 * title, so a date whose details are meant for the comp team does not spill
 * its body into a lock screen the way the comp board's own external copy is
 * deliberately kept content-free.
 */
/** "18:00" -> "6:00 PM". A phone alert is read at a glance; 24-hour time is
 *  not how this studio writes a call time anywhere else on screen. */
export function prettyEventTime(hhmm: string | undefined): string {
  const match = /^(\d{1,2}):(\d{2})$/.exec((hhmm ?? '').trim());
  if (!match) return '';
  const hour = Number(match[1]);
  if (!Number.isInteger(hour) || hour < 0 || hour > 23) return '';
  const suffix = hour < 12 ? 'AM' : 'PM';
  const display = hour % 12 === 0 ? 12 : hour % 12;
  return `${display}:${match[2]} ${suffix}`;
}

/** Event times are studio wall-clock values, never converted to the viewer's timezone. */
export function countdownEventTimeLabel(
  event: Pick<CountdownEventDoc, 'allDay' | 'startTime' | 'endTime'>,
  zoneLabel: 'CT' | 'Central' = 'CT',
): string {
  if (event.allDay === true) return 'All day';
  const start = isValidEventTime(event.startTime) ? prettyEventTime(event.startTime) : '';
  const end = isValidEventTime(event.endTime) ? prettyEventTime(event.endTime) : '';
  if (start && end) return `${start} to ${end} ${zoneLabel}`;
  if (start) return `${start} ${zoneLabel}`;
  if (end) return `Until ${end} ${zoneLabel}`;
  return '';
}

/**
 * WHAT AN ALERT SAYS ABOUT A DATE (Bobbie, 2026-08-26, correcting my first
 * cut, which carried only the name): "it should show date location and time."
 *
 * She is right, and the earlier version was over-careful. For a comp date the
 * WHEN and the WHERE are the whole message: a parent reading "Nationals" on a
 * lock screen still has to open the app to learn anything, which defeats the
 * alert. Date, time and venue are logistics the whole board can see anyway -
 * they are not the event's internals.
 *
 * STILL NEVER CARRIED: any long-form details or event-page panel body. Those
 * can contain room blocks, running orders and anything else, and none is a
 * one-line lock-screen fact - see the alert tests, which pin their absence.
 *
 * Every part is omitted when it is absent rather than guessed at, so a date
 * with no venue simply says less. Nothing here invents a studio fact.
 */
function eventFactsLine(dateLabel: string, timeLabel: string, venue: string): string {
  return [dateLabel, timeLabel, venue].filter(Boolean).join(' \u00b7 ');
}

export function countdownEventNotifyPayload(
  eventId: string,
  name: string,
  _visibility: CountdownVisibility,
  dateLabel: string,
  timeLabel = '',
  venue = ''
): NotifyPayload {
  const facts = eventFactsLine(dateLabel, timeLabel, venue);
  return {
    kind: 'calendar',
    title: `${name}: new date`,
    body: facts ? `${facts}. Open it for the details.` : 'Open it for the details.',
    // The event's own page (Bobbie, 2026-10-19: "all events should get a
    // page"). Alerts already sent keep their old /calendar/important-dates
    // #countdown- link, and that route and anchor stay, so they still land.
    link: countdownEventAlertLink(eventId),
  };
}

/** A TBD date has no event page yet, so its alert opens the list it is on. */
export const COUNTDOWN_TBD_ALERT_LINK = '/calendar/important-dates#date-tbd';

function tbdAlertLink(dateTbd: boolean, payload: NotifyPayload): NotifyPayload {
  return dateTbd ? { ...payload, link: COUNTDOWN_TBD_ALERT_LINK } : payload;
}

/**
 * Where a date alert opens: the event's own page. The server composes the
 * same link for its off-app copy (notificationAuthority.mjs
 * composeCountdownAlertContent); the two are pinned equal by the alert tests.
 */
export function countdownEventAlertLink(eventId: string): string {
  return `/events/${encodeURIComponent(eventId)}`;
}

/**
 * The same alert for an edit. Says UPDATED rather than new, because a family
 * reading "new date" about the trip they already answered about
 * has been told something false. Carries the current date so the commonest
 * edit of all - a date moving - is answerable from the notification itself.
 */
export function countdownEventUpdatedNotifyPayload(
  eventId: string,
  name: string,
  dateLabel: string,
  timeLabel = '',
  venue = ''
): NotifyPayload {
  const facts = eventFactsLine(dateLabel, timeLabel, venue);
  return {
    kind: 'calendar',
    title: `${name}: updated`,
    // "Now ..." because the commonest edit by far is a date or a time
    // moving, and the alert should answer that without being opened.
    body: facts ? `Now ${facts}. Open it for the details.` : 'Open it for the details.',
    link: countdownEventAlertLink(eventId),
  };
}

/**
 * The document a new countdown event is saved as, exactly as
 * useCreateCountdownEvent writes it. Pulled out of the hook so a server lane's
 * tests (tests/event-rsvp.test.mjs) can build their fixtures from the real
 * saved shape instead of a hand-made object. Throws the form's own errors.
 */
export function countdownEventCreateData(
  input: NewCountdownEventInput,
  createdBy: string,
  stamp: unknown,
): Record<string, unknown> {
  const name = input.name.trim().slice(0, 80);
  if (!name) throw new Error('Give the countdown a name.');
  const emoji = (input.emoji.trim() || '🏆').slice(0, 8);
  const dateTbd = input.dateTbd === true;
  if (!dateTbd && !isValidCountdownDate(input.date)) throw new Error('Pick a date.');
  const color = isValidHexColor(input.color) ? input.color : COUNTDOWN_COLOR_PRESETS[0].hex;
  const details = (input.details ?? '').trim();
  if (!isValidCountdownDetails(details)) {
    throw new Error(`Keep details under ${COUNTDOWN_DETAILS_MAX} characters.`);
  }
  const venue = (input.venue ?? '').trim();
  if (!isValidCountdownVenue(venue)) {
    throw new Error(`Keep the venue line under ${COUNTDOWN_VENUE_MAX} characters.`);
  }
  const endDate = dateTbd ? '' : (input.endDate ?? '').trim();
  if (endDate && !isValidCountdownDate(endDate)) throw new Error('Pick an end date.');
  if (endDate && endDate < input.date) throw new Error(COUNTDOWN_END_BEFORE_START);
  const data: Record<string, unknown> = {
    name,
    emoji,
    color,
    // A TBD date has no date field at all, never a placeholder day.
    ...(dateTbd ? {} : { date: input.date }),
    visibility: input.visibility,
    kind: input.kind,
    createdAt: stamp,
    createdBy,
    updatedAt: stamp,
  };
  if (input.startShowingDaysBefore != null) {
    data.startShowingDaysBefore = input.startShowingDaysBefore;
  }
  if (details) data.details = details;
  if (venue) data.venue = venue;
  // A same-day "range" is a single-day event, so the field stays absent and
  // the doc is byte-identical to one written before ranges existed.
  if (endDate && endDate > input.date) data.endDate = endDate;
  if (input.showOnClasses === true) data.showOnClasses = true;
  // Event model v2: only the keys the draft actually filled in, validated
  // with the same words the update door uses, packed under the ONE
  // eventV2 map the wire format uses (see normalizeEvent's own comment
  // for why the map exists at all). An all-empty draft writes NO eventV2
  // key, so a basic event stays byte-identical to a pre-v2 doc.
  const v2 = validatedEventModelV2Fields(
    dateTbd ? { ...input, repeat: undefined, repeatUntil: undefined } : input,
    {
      date: dateTbd ? '0000-01-01' : input.date,
      endDate: endDate && endDate > input.date ? endDate : undefined,
    }
  );
  if (dateTbd) v2.dateTbd = true;
  if (Object.keys(v2).length > 0) data.eventV2 = v2;
  return data;
}

/**
 * Q45 (DECISIONS 184): ask the server to send the Hub's comp list to the
 * Helper. Loaded on use and never awaited: the event is already saved, and a
 * page (or a test) that only uses the event helpers never links the sender.
 * With `edit`, only when that edit could change the list: a list field
 * changed and the event is or was a comp (eventEditChangesHubCompList).
 */
function requestHubCompsPublishLater(
  user?: User | null,
  edit?: { fields: Record<string, unknown>; previousKind: unknown },
): void {
  void import('./hubCompsPublishClient')
    .then((mod) => {
      if (edit && !mod.eventEditChangesHubCompList(edit.fields, edit.previousKind)) return;
      mod.requestHubCompsPublish(user);
    })
    .catch(() => undefined);
}

export function useCreateCountdownEvent() {
  const { user } = useAuth();
  return async (
    input: NewCountdownEventInput,
    options: { notifyAudience?: boolean; onNotify?: CountdownEventNotifyReport } = {}
  ): Promise<string> => {
    if (!db) throw new Error('Firestore not initialized.');
    if (!user) throw new Error('Sign in required.');
    const data = countdownEventCreateData(input, user.uid, serverTimestamp());
    const dateTbd = input.dateTbd === true;
    const ref = await addDoc(collection(db, 'countdownEvents'), data);
    // Q45 (DECISIONS 184): the Helper's comp list is the Hub's comp events.
    if (normalizeEventKind(input.kind) === 'competition') requestHubCompsPublishLater(user);

    // Tell the people this date is FOR. The date is already saved, so a
    // failure here is not a failed save - see reportCountdownEventNotify.
    if (options.notifyAudience !== false) {
      let recipientUids: string[];
      try {
        recipientUids = await countdownEventRecipients(input.visibility);
      } catch {
        // Codex round 7 on #805, finding 2: the Manage members overlay read
        // failed, so who is really still on the comp board could not be
        // confirmed. Say nothing rather than guess a roll-plus-staff
        // answer that might still include a director-removed member - the
        // exact shape updateCountdownEvent's own doc re-read failure
        // already uses below.
        reportCountdownEventOutcome(options.onNotify, null, 'could not confirm who is on the board to alert');
        return ref.id;
      }
      await reportCountdownEventNotify(
        recipientUids,
        tbdAlertLink(dateTbd, countdownEventNotifyPayload(
          ref.id,
          data.name as string,
          input.visibility,
          dateTbd ? COUNTDOWN_DATE_TBD_LABEL : prettyCountdownDate(input.date),
          // An all-day date has no clock to show, so it says so by omission
          // rather than printing a meaningless midnight.
          input.allDay === true ? '' : prettyEventTime(input.startTime),
          typeof data.venue === 'string' ? data.venue : ''
        )),
        options.onNotify,
        input.visibility === 'comp' ? ref.id : undefined
      );
    }
    return ref.id;
  };
}

/**
 * WHICH EDITS ARE WORTH WAKING A PHONE FOR.
 *
 * Bobbie, 2026-08-26: *"the Nationals event page that's part of the comp
 * board needs a notification anytime something is updated."* Taken utterly
 * literally that includes recolouring the card, and a studio that pings
 * fourteen families because somebody picked a different emoji teaches those
 * families to swipe the notification away - which costs exactly the trust
 * the "if you can see it, you get told about it" ruling is trying to buy.
 *
 * So: the fields that change what a family has to DO. When it is, where it
 * is, when the dancer has to be there, and how long it runs. Name is in
 * because the name IS the event on every card that renders it; a renamed
 * event reads as a different event.
 *
 * DELIBERATELY OUT: emoji, colour, `showOnClasses`, the display window, and
 * the details body. Details is the genuinely arguable one and it is out on
 * purpose - it is the field staff tidy repeatedly (a typo, a reworded
 * sentence), so it is the field most likely to turn this alert into noise.
 * If Bobbie wants details in, that is a one-line change here and a decision
 * she should make with that trade-off in front of her, not an inference.
 */
const COUNTDOWN_NOTIFYING_FIELDS = [
  'name',
  'date',
  'dateTbd',
  'endDate',
  'venue',
  'startTime',
  'endTime',
  'allDay',
  'callTime',
  'arriveMinutesBefore',
] as const;

/**
 * True when a patch changes something a family would want to be told about.
 * Presence in the patch is the test, not a before/after comparison: the one
 * shipped caller sends whole drafts, so comparing would need a read the
 * caller has already done, and re-saving an unchanged date is not a thing
 * that happens often enough to be worth the round trip.
 */
export function countdownEditWorthNotifying(fields: object): boolean {
  return COUNTDOWN_NOTIFYING_FIELDS.some((key) => key in fields);
}

/**
 * True when an edit CHANGES one of the notifying fields, comparing the saved
 * event against the new patch. The Important dates edit form sends the whole
 * draft, so presence alone (above) is true on every save; once that form's
 * send actually ran (2026-09-24, the optional-call fix), a recolour or an
 * emoji change alerted the whole audience. Opus review of 153c8bfcc.
 */
export function countdownEditChangesNotifyingFields(before: object, after: object): boolean {
  // Object keys are sorted before comparing: clearing a call time and
  // typing the same one back moves its key to the end, which is no change.
  const sorted = (value: unknown): unknown =>
    value && typeof value === 'object' && !Array.isArray(value)
      ? Object.fromEntries(Object.entries(value as Record<string, unknown>).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)))
      : value;
  const read = (source: object, key: string) =>
    JSON.stringify(sorted((source as Record<string, unknown>)[key] ?? null));
  // callTimes is how the edit form carries call times (COUNTDOWN_NOTIFYING_FIELDS
  // names the older single callTime), and a changed call time is exactly what
  // a family has to act on.
  return [...COUNTDOWN_NOTIFYING_FIELDS, 'callTimes'].some(
    (key) => key in after && read(before, key) !== read(after, key)
  );
}

export async function updateCountdownEvent(
  id: string,
  fields: Partial<
    Pick<
      CountdownEventDoc,
      | 'name'
      | 'emoji'
      | 'color'
      | 'date'
      | 'endDate'
      | 'showOnClasses'
      | 'visibility'
      | 'kind'
      | 'startShowingDaysBefore'
      | 'details'
      | 'venue'
      | 'dateTbd'
    >
  > &
    EventModelV2Input,
  options: {
    notifyAudience?: boolean;
    onNotify?: CountdownEventNotifyReport;
    /** The event's kind before this edit, so a non-comp edit never asks for a Helper publish (Q45). */
    previousKind?: unknown;
  } = {}
): Promise<void> {
  if (!db) throw new Error('Firestore not initialized.');
  const patch: Record<string, unknown> = { updatedAt: serverTimestamp() };
  // "Date to be confirmed": the doc ends up with NO date, NO end date and NO
  // repeat (a repeat needs a start), and eventV2.dateTbd set. Turning it OFF
  // needs a real date in the same patch, so an event can never be saved as a
  // dated event with no date.
  if (fields.dateTbd === true) {
    fields = { ...fields, date: undefined, endDate: undefined, repeat: undefined, repeatUntil: undefined };
    patch.date = deleteField();
    patch['eventV2.dateTbd'] = true;
  } else if (fields.dateTbd === false) {
    if (!isValidCountdownDate(fields.date)) throw new Error('Pick a date to turn off "Date to be confirmed".');
    patch['eventV2.dateTbd'] = deleteField();
  }
  if (fields.name !== undefined) patch.name = fields.name.trim().slice(0, 80);
  if (fields.emoji !== undefined) patch.emoji = (fields.emoji.trim() || '🏆').slice(0, 8);
  if (fields.color !== undefined) patch.color = fields.color;
  if (fields.date !== undefined) {
    if (!isValidCountdownDate(fields.date)) throw new Error('Pick a valid start date.');
    patch.date = fields.date;
  }
  if (fields.visibility !== undefined) patch.visibility = fields.visibility;
  if (fields.kind !== undefined) patch.kind = fields.kind;
  // 'always' choice on screen means "clear the field", not "write null" -
  // countdownEvents' rules shape only accepts the field absent or one of
  // the five presets, so a cleared window must delete the key.
  if ('startShowingDaysBefore' in fields) {
    patch.startShowingDaysBefore =
      fields.startShowingDaysBefore == null ? deleteField() : fields.startShowingDaysBefore;
  }
  if ('details' in fields) {
    const details = (fields.details ?? '').trim();
    if (!isValidCountdownDetails(details)) {
      throw new Error(`Keep details under ${COUNTDOWN_DETAILS_MAX} characters.`);
    }
    patch.details = details ? details : deleteField();
  }
  // Same clear-or-delete contract details has: rules accept the field
  // absent or a bounded string, never null or empty.
  if ('venue' in fields) {
    const venue = (fields.venue ?? '').trim();
    if (!isValidCountdownVenue(venue)) {
      throw new Error(`Keep the venue line under ${COUNTDOWN_VENUE_MAX} characters.`);
    }
    patch.venue = venue ? venue : deleteField();
  }
  // Same "clear it, do not null it" contract the display window has: the
  // rules shape accepts endDate absent or a valid isoDay on or after the
  // start, never null, so a single-day event DELETES the key.
  if ('endDate' in fields) {
    const endDate = (fields.endDate ?? '').trim();
    // A range is only checkable against its own start, so an endDate patch
    // must carry the start it belongs to. Without this an endDate-only caller
    // could aim an invalid write at the rules and get an opaque denial back;
    // the one shipped caller always sends the whole draft, so this guards a
    // sharp edge on an exported API rather than a live path.
    if (endDate && fields.date === undefined) {
      throw new Error('Send the start date with the end date so the two can be checked together.');
    }
    const start = fields.date !== undefined ? fields.date : null;
    if (endDate && !isValidCountdownDate(endDate)) throw new Error('Pick an end date.');
    if (endDate && start != null && endDate < start) {
      throw new Error(COUNTDOWN_END_BEFORE_START);
    }
    patch.endDate = endDate && start != null && endDate > start ? endDate : deleteField();
  }
  if ('showOnClasses' in fields) {
    patch.showOnClasses = fields.showOnClasses === true ? true : deleteField();
  }
  // Event model v2: every v2 key PRESENT on the patch either sets its
  // validated value or DELETES the field (the same clear-not-null contract
  // details, venue, and endDate record above); keys not present are left
  // alone. Cross-field laws need the fields they lean on in the SAME patch
  // (the endDate precedent): repeatUntil needs date, slug needs pageKind,
  // customTypeId needs eventType. The one shipped caller sends whole
  // drafts, so these guard the exported API's sharp edges.
  const v2Present = EVENT_MODEL_V2_FIELDS.filter((key) => key in fields);
  if (v2Present.length > 0) {
    if ((fields.repeatUntil ?? '').toString().trim() && fields.date === undefined) {
      throw new Error(
        'Send the start date with repeat-until so the two can be checked together.'
      );
    }
    const validated = validatedEventModelV2Fields(fields, {
      // repeatUntil is the only consumer of date here, and the guard above
      // proves date is present whenever repeatUntil is; this placeholder is
      // never compared against.
      date: fields.date !== undefined ? fields.date : '0000-01-01',
      endDate: 'endDate' in fields ? (fields.endDate ?? '').trim() || undefined : undefined,
    });
    // Dot-path writes into the ONE eventV2 map the wire format uses (see
    // normalizeEvent's own comment): a set writes eventV2.<key>, a clear
    // deletes exactly that subkey, keys not on the patch stay untouched,
    // and updateDoc creates the map on first write. An event that never
    // had a v2 field never gains an eventV2 key from a clear, because a
    // dot-path deleteField on an absent map is a no-op.
    for (const key of v2Present) {
      patch[`eventV2.${key}`] = key in validated ? validated[key] : deleteField();
    }
    // `validated` deliberately keeps an empty scheduleInfo. The dot-path
    // write stores that explicit override instead of deleting it, so clearing
    // Schedule cannot resurrect legacy `details`; the general details value
    // remains available to Important Dates, search and calendar feeds.
    // Self-parenting cannot be validated inside the shared helper (create
    // has no id yet); the update door knows its own id.
    if (patch['eventV2.parentEventId'] === id) {
      throw new Error('A date cannot live inside itself.');
    }
  }
  // PANEL EDITS ARE REMEMBERED, NOT ANNOUNCED. Dot-path arrayUnion so two
  // staff editing different panels never overwrite each other's mark.
  const panelKeysTouched = EVENT_PUBLISH_KEYS.filter((key) => key in fields);
  if (panelKeysTouched.length > 0) {
    patch['eventV2.unpublished'] = arrayUnion(...panelKeysTouched);
  }
  await updateDoc(doc(db, 'countdownEvents', id), patch);
  // Q45 (DECISIONS 184): a renamed, moved or re-kinded comp can change the
  // Helper's comp list. The server rebuilds the whole list from every event.
  requestHubCompsPublishLater(undefined, { fields: fields as Record<string, unknown>, previousKind: options.previousKind });

  // Tell the people this date is for that it MOVED. Same never-throws
  // contract as the create path: the edit is already saved by the time this
  // runs. The audience is read from the doc rather than the patch, because an
  // edit that does not touch visibility does not carry it, and guessing
  // 'comp' would quietly notify the wrong roll.
  // THE POLICY IS THE DEFAULT. Left undefined, the field list above decides
  // (a moved date notifies, a recolour does not). A caller may pass false to
  // silence it; passing true never makes a panel edit alert (see below).
  // N11: the alert needs a date/time/venue-class field in the patch no matter
  // what the caller passed. A pure panel edit (schedule, hotel, travel,
  // about) can never alert; it waits for publishCountdownEventUpdates.
  if (countdownEditWorthNotifying(fields)
      && (options.notifyAudience ?? true) === true) {
    await notifyCountdownEventUpdated(id, fields, options.onNotify);
  }
}

/**
 * Record that event-page parts changed without telling anyone. For the
 * server-written panel photo, which the client cannot mark in the same write.
 * A failed mark is not a failed photo save; it throws only to the caller who
 * chooses to care.
 */
export async function markCountdownEventUnpublished(
  id: string,
  keys: readonly EventPublishKey[]
): Promise<void> {
  if (!db || keys.length === 0) return;
  // updatedAt is required by the rules (auditKeptNow) on every event update.
  await updateDoc(doc(db, 'countdownEvents', id), {
    'eventV2.unpublished': arrayUnion(...keys),
    updatedAt: serverTimestamp(),
  });
}

/**
 * PUBLISH UPDATES: the ONE alert for everything staff changed on the page.
 * The mark is cleared in a transaction BEFORE the send, so a double tap (or
 * two staff) finds it already empty and sends nothing. If the send itself
 * fails the mark is put back so staff can try again; an empty audience is
 * not a failure and stays cleared. Never throws on the notify half.
 */
export async function publishCountdownEventUpdates(
  id: string,
  options: { onNotify?: CountdownEventNotifyReport } = {}
): Promise<{ published: boolean; keys: EventPublishKey[]; restored?: boolean; uncertain?: boolean }> {
  if (!db) throw new Error('Firestore not initialized.');
  const ref = doc(db, 'countdownEvents', id);
  const keys = await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    const waiting = normalizeEvent(id, snap.data() ?? {}).unpublished ?? [];
    if (waiting.length === 0) return [] as EventPublishKey[];
    tx.update(ref, { 'eventV2.unpublished': deleteField(), updatedAt: serverTimestamp() });
    return waiting;
  });
  if (keys.length === 0) return { published: false, keys };
  let failed = false;
  let uncertain = false;
  await notifyCountdownEventUpdated(id, {}, (result, detail) => {
    // The transport can RETURN a failure instead of throwing, and it also
    // returns serverReached:false or a 5xx AFTER the server delivered (the
    // 45s abort, a lost response, a gateway 502/504 mid-send). Three shapes:
    //  - clean refusal (4xx): nothing went out;
    //  - UNKNOWN (no status, or 5xx): the alert may already have reached
    //    families, so the keys come back but the screen must not say it
    //    "did not go out" (countdown sends have no dedup);
    //  - a reply that delivered nothing with errors: failed.
    if (result && !result.queued) {
      const status = result.refusedStatus;
      const refused = typeof status === 'number' && status >= 400 && status < 500;
      const unknown = !refused && (result.serverReached === false || (typeof status === 'number' && status >= 500));
      const errored = !refused && !unknown && result.errors > 0
        && result.inApp + result.email + result.sms + result.push === 0;
      if (refused || unknown || errored) {
        failed = true;
        uncertain = unknown;
        reportCountdownEventOutcome(options.onNotify, null,
          refused ? `the server refused it (${status})`
            : unknown ? 'the server did not confirm' : 'it could not be delivered');
        return;
      }
    }
    if (result === null && detail !== 'nobody to alert about this date') failed = true;
    reportCountdownEventOutcome(options.onNotify, result, detail);
  });
  if (failed) {
    // `restored` tells the caller whether the waiting list is back, so the
    // screen never says "still waiting" about a list that is gone.
    let restored = true;
    try { await markCountdownEventUnpublished(id, keys); } catch { restored = false; }
    return { published: false, keys, restored, ...(uncertain ? { uncertain: true } : {}) };
  }
  return { published: true, keys };
}

/**
 * B32: A PHOTO SAVED ON THE EVENT PAGE IS AN UPDATE, AND IT ALERTS LIKE ONE.
 *
 * Bobbie reported Kim added a photo under a comp event's About section and
 * it never reached anybody (PR #1146, sent back 2026-09-16 and rebuilt here
 * on current code). The standing rule is DECISIONS.md "IF YOU CAN SEE IT,
 * YOU GET TOLD ABOUT IT": the event page "needs a notification anytime
 * something is updated."
 *
 * The photo itself is written by the server gateway (messageAttachmentWrite
 * .mjs, countdownEventPanelPhoto), which stamps a fresh updatedAt in the
 * same transaction. Call this only AFTER that save succeeded. It writes
 * nothing; it rides the exact lane a ticked "Let the team know" text save
 * rides: the same audience (countdownEventRecipients, from the event's own
 * visibility), the same comp board authority and mute, and for a comp date
 * the same server-composed copy and per-save notification id, so a retried
 * request for one save rings once. Never throws, same as the edit path.
 */
export async function notifyCountdownEventPhotoSaved(
  id: string,
  options: { onNotify?: CountdownEventNotifyReport } = {}
): Promise<void> {
  if (!db) {
    reportCountdownEventOutcome(options.onNotify, null, 'could not reach the studio database');
    return;
  }
  await notifyCountdownEventUpdated(id, {}, options.onNotify);
}

/**
 * The "updated" alert for one already-saved event. Shared by the text edit
 * path above and the photo path just above, so the two can never drift onto
 * different audiences or wording.
 */
async function notifyCountdownEventUpdated(
  id: string,
  fields: Partial<Pick<CountdownEventDoc, 'name' | 'date' | 'dateTbd' | 'visibility' | 'venue'>> &
    Pick<EventModelV2Input, 'startTime' | 'allDay'>,
  onNotify: CountdownEventNotifyReport | undefined
): Promise<void> {
  if (!db) return;
  let visibility: CountdownVisibility | null =
    fields.visibility !== undefined ? fields.visibility : null;
  let name = fields.name !== undefined ? fields.name.trim().slice(0, 80) : '';
  let date = fields.date !== undefined ? fields.date : '';
  let venueLine = fields.venue !== undefined ? (fields.venue ?? '').trim() : '';
  let startTime = fields.startTime !== undefined ? (fields.startTime ?? '') : '';
  let allDay = fields.allDay === true;
  let dateIsTbd = fields.dateTbd === true;
  // The doc is re-read whenever the patch does not carry everything the
  // alert says. Bobbie, 2026-08-26: "it should show date location and
  // time" - so a patch that moves only the time still has to state WHERE,
  // and one that moves only the venue still has to state WHEN.
  const needsRead = !visibility || !name || !date
    || fields.venue === undefined || fields.startTime === undefined;
  if (needsRead) {
    try {
      const snap = await getDoc(doc(db, 'countdownEvents', id));
      const current = (snap.data() ?? {}) as Partial<CountdownEventDoc>;
      const currentV2 = normalizeEvent(id, snap.data() ?? {});
      visibility = visibility ?? (current.visibility ?? 'comp');
      name = name || String(current.name ?? '').slice(0, 80);
      date = date || String(current.date ?? '');
      if (fields.dateTbd === undefined) dateIsTbd = currentV2.dateTbd === true;
      if (fields.venue === undefined) venueLine = String(current.venue ?? '').trim();
      if (fields.startTime === undefined) startTime = currentV2.startTime ?? '';
      if (fields.allDay === undefined) allDay = currentV2.allDay === true;
    } catch {
      // Could not re-read the event. Rather than notify a guessed audience,
      // say nothing: a wrong-roll alert about somebody else's date is worse
      // than a missed one, and the edit itself is already saved.
      reportCountdownEventOutcome(onNotify, null, 'could not read the date back to see who to tell');
      return;
    }
  }
  // needsRead above covers the null case, but that is a fact about a
  // separate boolean and the compiler cannot see it. Bail rather than
  // cast: an alert with no resolved audience should not be sent at all.
  if (!visibility) {
    reportCountdownEventOutcome(onNotify, null, 'could not tell who this date is for');
    return;
  }
  let recipientUids: string[];
  try {
    recipientUids = await countdownEventRecipients(visibility);
  } catch {
    // Codex round 7 on #805, finding 2: same fail-closed shape as the doc
    // re-read failure just above - the Manage members overlay could not
    // be read, so who is really still on the comp board could not be
    // confirmed. Say nothing rather than guess.
    reportCountdownEventOutcome(onNotify, null, 'could not confirm who is on the board to alert');
    return;
  }
  await reportCountdownEventNotify(
    recipientUids,
    tbdAlertLink(dateIsTbd, countdownEventUpdatedNotifyPayload(
      id,
      name,
      dateIsTbd ? COUNTDOWN_DATE_TBD_LABEL : prettyCountdownDate(date),
      allDay ? '' : prettyEventTime(startTime),
      venueLine
    )),
    onNotify,
    visibility === 'comp' ? id : undefined
  );
}

/**
 * Where a countdown pill goes when someone taps it. The rule itself lives in
 * countdownEventHref.ts, which imports nothing so that its test can RUN it
 * instead of reading it - see that file for why that mattered. Re-exported
 * here because this is where every caller already looks for it.
 */
export { countdownEventHref } from './countdownEventHref';
